import { addHours, addMinutes, format, formatDistanceToNow } from "date-fns";
import { it } from "date-fns/locale";
import useLabels from "../../hooks/useLabels";
import { ChatArea, Input, Label, Switch } from "../FormComponents";
import { Player } from "@lottiefiles/react-lottie-player";
import json_arrow_black from "../../assets/lottieJson/black_arrow.json";
import { useEffect, useRef, useState } from "react";
import AnalysisEvolution from "../AnalysisEvolution/AnalysisEvolutions";
import useAuth from "../../hooks/useAuth";
import { ROLES } from "../../constants/base";
import TableEdit from "../TableEdit";
import { createColumnHelper } from "@tanstack/react-table";
import TableCell from "../TableEdit/TableCell";
import {
  ConfirmationModal,
  ContextMenu,
  CTA,
  FilterIcon,
  OperationRecap,
  OptionalNoteRow,
  TankOperationDetail,
  Tooltip,
} from "../Generic";
import { empty } from "../../context/operations/operationsSlice";
import { toast } from "react-toastify";
import {
  addOperationNote,
  getProtocols,
  getWarehouseByType,
} from "../../services/utils";
import { socket } from "../../socket";
import QRcodeReader from "../QRcodeReader";
import {
  selectAnalysisDisplayGrid,
  selectWarehouseByType,
  selectWineMaking,
} from "../../context/selectors";
import { toggleAnalysisDisplayGrid } from "../../context/configs/configsSlice";
import { useSelector } from "react-redux";
import MultipleRowsCell from "../TableEdit/MultipleRowsCell";
import { color } from "framer-motion";
import { setWineMaking } from "../../context/protocols/wineMakingSlice";
import {
  aggregateDrugsById,
  computeDynamicStabilizationEndDate,
  computeFlowRates,
  computeModifiedDrugBatches,
  computeRequiredNumericQty,
  computeRequiredWarehouseQty,
  getElapsedTimeWithClass,
  getStartDate,
  getStartedOperationTimingInfo,
  getTreatmentColumns,
  getTreatmentTimingDisplay,
  getWarehouseUnit,
  getWineColor,
  getWineColorClassName,
  groupDrugBatchesBySubstance,
} from "../../services/common";
import { setWarehouse } from "../../context/warehouse/warehouseSlice";
import { useContextMenu } from "../../hooks/useContextMenu";
import EditCell from "../TableEdit/EditCell";
import { Icon } from "../../components/Icon";

// import QRcodeReader from "../QRcodeReader";

const computeTreatmentsData = (type, operation = {}, treatments = []) => {
  const tmp = [];
  if (type === "TREATMENT" || type === "DESTEMMING") {
    const key = type === "DESTEMMING" ? 'arrow_quantity' : 'old_quantity';
    const liters =
      operation?.dest_tanks?.reduce((acc, curr) => {
        return acc + (Number(curr?.[key]) || 0);
      }, 0) || 0;
      
    treatments?.drugs
      ?.sort((a, b) => a?.substance_id - b?.substance_id)
      ?.forEach((treatment) => {
        const newQuantity = operation?.treatments?.find(
          (o) => o?.substance_id == treatment?.substance_id
        )?.quantity;

        const total_quantity = computeTotalProductToAdd(treatment, liters);
        const drug_batches =
          groupDrugBatchesBySubstance(
            operation?.treatments || [],
            treatment?.substance_id
          )?.[treatment?.substance_id] || [];

        tmp.push({
          ...treatment,
          drug_batches,
          quantity: newQuantity || treatment?.quantity,
          total_quantity: parseFloat(total_quantity),
        });
      });
    } else if (type === "CLEANING") {
      const seenDays = new Set();
      for (const tr of treatments?.operations || []) {
        if (seenDays.has(tr.day)) continue;
        seenDays.add(tr.day);

        tmp.push({
          product: tr?.product || tr?.substance_id,
          product_name: tr?.product_name || tr?.substance_name,
          substance_id: tr?.product || tr?.substance_id,
          substance_name: tr?.product_name || tr?.substance_name,
          quantity: tr?.quantity,
          type: tr?.product_type,
          work_time: tr?.work_time,
          day: tr?.day,
          phase: tr?.phase,
          operation: tr?.operation,
          unit: tr?.product_unit || "",
          // ...(tr?.operation === 'HOMOGENIZATION_MASS' && {
          //   protocol_type: tr?.protocol_type,
          //   homogenization_type: tr?.homogenization_type,
          //   homogenization_motor_speed: tr?.homogenization_motor_speed,
          //   homogenization_pause_time: tr?.homogenization_pause_time,
          //   iter_per_day: tr?.iter_per_day,
          // })
        });
      }
    }

  return tmp;
}

export const formatPomaces = (totalPomaces, getLabel) => {
  const kg = parseInt(totalPomaces);

  // If <= 100 kg → stay in Kg
  if (kg <= 100) {
    return {
      value: kg,
      unit: "Kg",
      label: "Kg",
    };
  }

  // Convert to quintali
  const quintali = kg / 100;
  
  // Convert to Kgs
  // const quintali = kg / 100  * 0.4;
  
  const value = quintali.toFixed(quintali % 1 === 0 ? 0 : 2); // 1 or 1.25 etc

  const unit =
    quintali === 1
      ? getLabel("operationPomaces100Kg") // "quintale"
      : getLabel("operationPomaces100Kgs"); // "quintali"

  return {
    value,
    unit,
    label: unit,
  };
};

const renderPomace = (pomace, value, unit, index, getLabel = () => null) => {
  return (
    <div className="pomace_wrapper" key={`pomace_${index}`}>
      <Label label={getLabel("operationPomacesDestinationPlaceholder")}>
        {pomace?.recipient || ""}{" "}
        {`(${pomace?.quantity} L ≈ ${value} ${unit})`}
      </Label>
      <Label label={getLabel("operationDestPomaceReason")}>
        {pomace?.waste_type
          ? getLabel(`pomaceReason_${pomace?.waste_type}`) || ""
          : ""}
      </Label>
      {Number(pomace?.cost) > 0 && (
        <Label label={getLabel("operationDestPomacesCost")}>
          {pomace?.cost || "—"} €
        </Label>
      )}
      {pomace?.notes && (
        <OptionalNoteRow isDetail={true} notes={pomace?.notes || ""} />
      )}
    </div>
  )
}

const renderPomaces = (operation, getLabel) => {
  // 100 L of fresh pomace (density ≈ 0.4 kg/L)   --> 40 Kg
  // 100 L of partially dried pomace (~0.55 kg/L) --> 55 Kg
  // 100 L of dry pomace (~0.8 kg/L)              --> 80 Kg
  const pomaces = operation?.pomaces || [];

  if (!Array.isArray(pomaces) || pomaces.length === 0) return null;

  return <div className="pomaces_wrapper">
    {pomaces.map((pomace, index) => {
      const totalPomaces = Number(pomace?.quantity) || 0;
      if (totalPomaces <= 0) return null;

      const { value, unit } = formatPomaces(totalPomaces, getLabel);

      return (
        renderPomace(pomace, value, unit, index, getLabel)
      )
    })}
  </div>
};


const computeTotalProductToAdd = (drug, liters) => {
  const productName = drug?.substance_name || null;
  const productQuantity = Number(drug?.quantity) || 0;
  const unit_id = drug?.unit_id || null;
  if (
    liters === 0 ||
    productQuantity === 0 ||
    unit_id === null ||
    productName === null
  )
    return false;

  let totalProductToAdd = 0;
  switch (unit_id) {
    case 1: // mg/L
      const tmpQuantity1 = parseFloat(liters * productQuantity);
      if (tmpQuantity1 >= 1000 * 1000)
        totalProductToAdd =
          parseFloat(tmpQuantity1 / 1000 / 1000).toFixed(2) + " Kg";
      else if (tmpQuantity1 >= 1000)
        totalProductToAdd = parseFloat(tmpQuantity1 / 1000).toFixed(2) + " g";
      else totalProductToAdd = tmpQuantity1.toFixed(2) + " mg";
      break;
    case 2: // g/hL
      const tmpQuantity2 = parseFloat((liters / 100) * productQuantity);
      if (tmpQuantity2 >= 1000)
        totalProductToAdd = parseFloat(tmpQuantity2 / 1000).toFixed(2) + " Kg";
      else totalProductToAdd = parseFloat(tmpQuantity2).toFixed(2) + " g";
      break;
    case 3: // L
      break;
    case 4: // Kg
      break;
    case 5: // " "
      break;
    case 6: // g/L
      break;
    case 7: // g/100Kg
      const tmpQuantity7 = parseFloat((liters / 100) * productQuantity);
      if (tmpQuantity7 >= 1000)
        totalProductToAdd = parseFloat(tmpQuantity7 / 1000).toFixed(2) + " Kg";
      else totalProductToAdd = parseFloat(tmpQuantity7).toFixed(2) + " g";
      break;
    case 8: // NTU
      totalProductToAdd =
        parseFloat(liters * productQuantity).toFixed(2) + " NTU";
      break;
    case 9: // %Vol.
      totalProductToAdd =
        parseFloat(liters * productQuantity).toFixed(2) + " %Vol.";
      break;
    case 10: // uS/DIT
      totalProductToAdd =
        parseFloat(liters * productQuantity).toFixed(2) + " uS/DIT";
      break;
    default:
      break;
  }
  return totalProductToAdd;
};

const renderCost = (cost = null) => {
  if (cost == null) return "-";

  if (cost > 0 && cost < 0.01) {
    return "≈0.00 €";
  }

  return `${cost.toFixed(2)} €`;
};

const renderTotal = (tanks, operation, tank_id, isShowCost, watchedDrugBatches, getLabel) => {
  if (operation?.type === "CLEANING" || operation?.type === "NEW_LOT") return "";
  if (operation?.type === "DESTEMMING" && !(operation?.src_tanks?.[0]?.color === "RED" && ["CARBONIC_MACERATION", "ROSE_MACERATION"].includes(operation?.subtype))) return "";
  
  const isBottling = operation?.type === "BOTTLING";

  const mainSrc = operation?.src_tanks?.[0] || null;
  if (mainSrc === null) return "";
  const liters =
    operation?.type === "BOTTLING"
      ? operation?.dest_tanks?.find((t) => t?.transfer_id == tank_id)?.quantity
      : operation?.type === "DESTEMMING"
      ? operation?.dest_tanks?.find((t) => t?.tank_id == tank_id)?.arrow_quantity
      : operation?.completion_date !== null
      ? operation?.src_tanks?.find((t) => t?.tank_id == tank_id)?.old_quantity
      : operation?.src_tanks?.find((t) => t?.tank_id == tank_id)?.preOperation
          ?.tank?.quantity ||
        tanks?.find((t) => Number(t.id) === Number(tank_id))?.quantity ||
        0;

  const totalDrugs = [];
  let totalCostPerTank = 0;

  operation?.treatments
    ?.filter((t) =>
      isBottling ? (t?.operation === "ADD" && t.transfer_details_id === tank_id) : t?.tank_id === tank_id
    ) // when BOTTLING, skip CIP protocol's products
    ?.forEach((drug) => {
      const productName = drug?.substance_name || null;
      const totalProductToAdd = computeTotalProductToAdd(drug, liters);

      const unit = [1, 2, 6, 7].includes(drug?.unit_id)
        ? "Kg"
        : drug?.unit_id === 3
        ? "L"
        : drug?.unit;

      const modifiedBatches = watchedDrugBatches?.[drug?.substance_id] || [];
      const effectiveBatches =
        (Array.isArray(modifiedBatches) && modifiedBatches?.length > 0
          ? modifiedBatches
          : drug?.drug_batches) || [];
      
      const drugBatches =
        effectiveBatches?.map((b) => {
          return {
            batch_number: b?.batch_number,
            ...(drug?.drug_batches?.length > 1 && {
              quantity: b?.quantity,
              unit,
            }),
          };
        }) || [];

      if (totalProductToAdd) {
        let cost = 0;
        for (const b of drug?.drug_batches || []) {
          const multiplier = unit === "Kg" || unit === "L" ? 1000 : 1;
          if (b?.unitary_cost && b?.quantity)
            cost += parseFloat(
              (Number(b?.unitary_cost * multiplier) || 0) *
                Number(b?.quantity) || 0
            );
        }

        const numericCost = Number(cost);

        const normalizedCost =
          Number.isFinite(numericCost) && numericCost > 0 ? numericCost : null;
        totalDrugs.push({
          quantity: totalProductToAdd,
          substance_name: productName,
          cost: normalizedCost,
          drug_batches: drugBatches,
        });

        if (numericCost > 0) {
          totalCostPerTank += normalizedCost;
        }
      }
    });

  if (Object.keys(totalDrugs)?.length === 0) return;
  
  return (
    <div className="total_drug_wrapper">
      <div className="total_drug">
        {getLabel("drugTotal")}{" "}
        <ul>
          {totalDrugs?.map((totalDrug, index) => {
            return (
              <li key={`total_drug_${index}`} className="total_drug_quantity">
                <span className="substance_quantity">
                  {totalDrug?.quantity}
                </span>
                <span className="substance_name">
                  {totalDrug?.substance_name}
                </span>

                {totalDrug?.drug_batches?.length > 0 && (
                  <p className="substance_batch_names">
                    (
                    {totalDrug?.drug_batches?.map((batch, index) => {
                      return (
                        <span key={`drug_batch-${index}`}>
                          {batch?.batch_number}
                          {batch?.quantity && batch?.unit
                            ? ` ${batch?.quantity} ${batch?.unit}${index < totalDrug?.drug_batches?.length - 1 ? ", " : ""}`
                            : ""}
                        </span>
                      );
                    })}
                    )
                  </p>
                )}

                {index === totalDrugs?.length - 1 ? "" : ""}
              </li>
            );
          })}
        </ul>
      </div>
      {isShowCost && (
        <div className="total_drug_cost_wrapper">
          <div className="total_drug">
            {getLabel("drugTankCosts")}{" "}
            {Number(totalCostPerTank) > 0
              ? ` (${parseFloat(totalCostPerTank).toFixed(2)} €)`
              : ""}
            <ul>
              {totalDrugs?.map((totalDrug, index) => {
                return (
                  <li
                    key={`total_drug_cost_${index}`}
                    className={`total_drug_cost ${totalDrug?.drug_batches?.length > 0 ? "fix-margin" : ""}`}
                  >
                    <span>{renderCost(totalDrug.cost)}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

const getRecapTanks = (tanks, operation, isSrc = true) => {
  const tankList = [];

  const isCompleted = operation?.completion_date !== null;
  const tmpTanks = (isSrc ? operation?.src_tanks : operation?.dest_tanks) || [];

  tmpTanks?.forEach((t) => {
    const tank = tanks?.find((s) => s?.id === t?.tank_id);
    if (isCompleted) {
      if (isSrc) {
        // tankList.push({ tankData: {...t?.postOperation?.tank, lot: t?.postOperation?.tank?.batch_name, quantity: t?.quantity - t?.postOperation?.tank?.arrow_quantity }, new_quantity: String(t?.postOperation?.tank?.quantity), toRemove: t?.postOperation?.tank?.arrow_quantity, quantity: Math.abs(t?.postOperation?.tank?.arrow_quantity), capacity: t?.postOperation?.tank?.capacity, lot: t?.postOperation?.tank?.batch_name, name: t?.postOperation?.tank?.name, color: t?.postOperation?.tank?.color || t?.postOperation?.batch?.color })

        const tank = {
          ...t,
          remove_quantity: t?.arrow_quantity,
          is_src: isSrc,
        };  
        if (operation?.type === "RACKING") {
          const pomacesQuantity = Number(operation?.pomaces?.[0]?.quantity) || 0;
          tank.quantity -= pomacesQuantity;
          tank.arrow_quantity += pomacesQuantity;
        }
        tankList.push(tank);
      } else {
        const drop = operation?.dest_tanks?.[0]?.drop;

        const tank = {
          ...t,
          ...(operation?.type === "RACKING" && {
            quantity: (Number(t?.old_quantity) || 0) + (Number(t?.arrow_quantity) || 0),
          }),
          add_quantity:
            (operation?.type === "CUT"
              ? operation?.src_tanks?.reduce((acc, curr) => {
                  return acc + (Number(curr?.arrow_quantity) || 0);
                }, 0) || 0
              : t?.arrow_quantity) - drop,
          is_src: isSrc,
        };

        tankList.push(tank);

        // tankList.push({ tankData: {...t?.postOperation?.tank, quantity: t?.postOperation?.tank?.old_quantity, value: true }, quantity: t?.new_quantity - (operation?.type === 'RACKING' ? t?.postOperation?.tank?.drop : 0), batch_name_selector: { value: false, label: t?.new_batch_name}, drop: 0*t?.postOperation?.tank?.drop, ...(operation?.type === 'RACKING' && { batch_type: t?.batch_type }), lot: t?.postOperation?.tank?.batch_name, color: t?.postOperation?.tank?.color || t?.postOperation?.batch?.color })
      }
      // TO DO RACKING con cali non funziona, forse non va mai quando ci sono cali
    } else {
      if (isSrc) {
        if (operation?.type === "CUT") {
          const isPlanned = tank?.is_planned_src === true;
          const isPlannedByOtherOps =
            isPlanned &&
            tank?.programmed_as_src?.some(
              (p_id) => p_id !== Number(operation?.id)
            );

          const planned_quantity =
            Number(tank?.quantity) -
            Number(tank?.planned_quantity) -
            Number(t?.arrow_quantity);
          const removed_quantity =
            Number(tank?.quantity) - Number(t?.arrow_quantity);

          tankList.push({
            name: tank?.name,
            color: tank?.color,
            capacity: tank?.capacity,
            quantity: removed_quantity - (isPlanned ? planned_quantity : 0),
            remove_quantity: Number(t?.arrow_quantity),
            batch_name: tank?.batch_name,
            is_planned: isPlanned && isPlannedByOtherOps,
            planned_quantity: isPlanned ? planned_quantity : 0,
            is_src: isSrc,
          });
        }
        // if (operation?.type === 'CUT') tankList.push({ tankData: { ...tank, quantity: Number(t?.preOperation?.tank?.old_quantity), color: t?.preOperation?.batch?.color }, ...tank, quantity: Number(t?.quantity) })
        // else tankList.push({ tankData: {...tank }, ...tank })
        else {
          const pomacesQuantity =
            (operation?.type === "RACKING")
              ? Number(operation?.pomaces?.[0]?.quantity) || 0
              : 0;
          const quantityToRemove =
            operation?.type === "CLOSING" || operation?.type === "RACKING"
              ? operation?.dest_tanks?.reduce((acc, curr) => {
                  return acc + (Number(curr?.arrow_quantity) || 0);
                }, 0) || 0
              : 0;

          const tmp = {
            ...t,
            quantity: t?.quantity - quantityToRemove - pomacesQuantity,
          };
          const isPlannedState = tank?.is_planned_src === true;
          const programmedOpIds = tank?.programmed_as_src;
          const isPlannedByOtherOps =
            isPlannedState &&
            programmedOpIds?.some((p_id) => p_id !== Number(operation?.id));
          if (isPlannedState && isPlannedByOtherOps) {
            const plannedPomacesQuantity =
              tank?.planned_pomaces?.reduce((acc, curr) => {
                return acc + (Number(curr?.quantity) || 0);
              }, 0) || 0;

            tmp.is_planned = true;
            tmp.quantity = tank?.planned_quantity - plannedPomacesQuantity; // final quantity
            tmp.planned_quantity =
              tmp.old_quantity - tmp.arrow_quantity - tmp.quantity;
          }
          tankList.push({
            ...tmp,
            is_src: isSrc,
            // quantity: t?.quantity - quantityToRemove - pomacesQuantity,
            arrow_quantity: t?.arrow_quantity + pomacesQuantity,
            ...(operation?.type === "RACKING" && {
              pomaces_quantity: pomacesQuantity,
            }),
          });
        }
      } else {
        if (operation?.type === "CUT") {
          const drop = operation?.dest_tanks?.[0]?.drop;
          const add_quantity =
            (operation?.src_tanks?.reduce((acc, curr) => {
              return acc + (Number(curr?.arrow_quantity) || 0);
            }, 0) || 0) - drop;
          const isPlanned = tank?.is_planned === true;
          const isPlannedByOtherOps =
            isPlanned &&
            tank?.programmed_as_dest?.some(
              (p_id) => p_id !== Number(operation?.id)
            );
          // const planned_quantity = Number(tank?.quantity) - Number(t?.arrow_quantity);
          const planned_quantity =
            Number(tank?.quantity) +
            Number(tank?.planned_quantity) -
            Number(t?.arrow_quantity);

          tankList.push({
            name: tank?.name,
            color: tank?.color || operation?.src_tanks?.[0]?.color,
            capacity: tank?.capacity,
            quantity: (isPlanned ? tank?.quantity : 0) + add_quantity,
            // quantity: add_quantity + (isPlanned ? planned_quantity : 0),
            add_quantity: add_quantity,
            batch_name: tank?.batch_name || tank?.planned_batch_name,
            is_planned: isPlanned,
            planned_quantity:
              isPlanned && isPlannedByOtherOps ? planned_quantity : 0,
            is_src: isSrc,
          });
        }

        // TO DO fix batch_type in RACKING and what if src is not emptied by the operation?
        // if (operation?.type === 'CUT' && false) tankList.push({ tankData: {...tank, toAdd: t?.quantity, quantity: Number(tank?.quantity), value: true, color: operation?.src_tanks?.[0]?.preOperation?.batch?.color }, quantity: t?.quantity, batch_name_selector: { value: false, label: t?.new_batch_name}, lot: tank?.batch_name })
        // else tankList.push({ tankData: {...tank, toAdd: t?.quantity, quantity: Number(tank?.quantity), value: true }, quantity: t?.quantity, batch_name_selector: { value: false, label: t?.new_batch_name}, ...(operation?.type === 'RACKING' && { batch_type: t?.batch_type }), lot: tank?.batch_name, color: tank?.color })
        // else tankList.push({ quantity: t?.quantity, batch_name_selector: { value: false, label: t?.new_batch_name}, ...(operation?.type === 'RACKING' && { batch_type: t?.batch_type }), lot: tank?.batch_name, color: tank?.color })
        else {
          const isPlanned = tank?.is_planned === true;
          const isPlannedByOtherOps =
            isPlanned &&
            tank?.programmed_as_dest?.some(
              (p_id) => p_id !== Number(operation?.id)
            );

          const planned_quantity = isPlannedByOtherOps
            ? Number(tank?.planned_quantity) - Number(t?.arrow_quantity)
            : 0;

          tankList.push({
            ...t,
            quantity: t?.old_quantity + t?.arrow_quantity,
            // add_quantity: t?.quantity,
            add_quantity: t?.arrow_quantity,
            is_planned: isPlannedByOtherOps,
            planned_quantity: planned_quantity,
            is_src: isSrc,
          });
        }
      }
    }
  });

  if (operation?.type === "RACKING") {
    const pomaces = operation?.pomaces?.[0] || {};
    if (pomaces?.recipient)
      tankList.push({
        type: "pomaces",
        name: pomaces?.recipient || "",
        notes: pomaces?.notes || "",
        quantity: Number(pomaces?.quantity) || 0,
        cost: Number(pomaces?.cost) || 0,
        waste_type: pomaces?.waste_type || null,
        is_src: false,
      });
  }

  return tankList;
};

export const DestLotDetail = ({
  lot,
  operation,
  toRemove = null,
  isName = true,
  isRecap = false,
  isDestemming = false,
  isNewLot = false,
  isAdditional = false,
  isMobile = false,
  isDetail = false,
  isWizardMode = false,
}) => {
  const [getLabel] = useLabels();
  const [lotData, setLotData] = useState({});

  useEffect(() => {
    const tmp = {};
    tmp.name = lot?.name;
    tmp.arrow_quantity = lot?.arrow_quantity;
    tmp.color = lot?.color;

    tmp.preHeigth = `${
      (Number(lot?.real_initial_quantity) / Number(lot?.initial_quantity)) * 100
    }%`;
    tmp.preEstimatedBottom = tmp.preHeigth;
    tmp.preEstimatedHeigth = `${
      (Number(lot?.estimated_initial_quantity) /
        Number(lot?.initial_quantity)) *
      100
    }%`;

    tmp.old_quantity = `${Number(lot?.real_initial_quantity)} / ${
      lot?.initial_quantity
    } Kg`;
    tmp.old_occupancy = ` (${Math.round(
      (Number(lot?.real_initial_quantity) * 100) / Number(lot?.initial_quantity)
    )}%)`;

    tmp.postHeigth = `${
      (Number(lot?.real_final_quantity) / Number(lot?.initial_quantity)) * 100
    }%`;
    tmp.postEstimatedHeight = tmp.preEstimatedHeigth;
    tmp.new_quantity = `${Number(lot?.real_final_quantity) || 0} / ${
      lot?.initial_quantity
    } ${isNewLot ? "L" : "Kg"}`;
    tmp.new_occupancy = ` (${Math.round(
      ((Number(lot?.real_final_quantity) || 0) * 100) /
        Number(lot?.initial_quantity)
    )}%)`;
    tmp.postEstimatedBottom = tmp.postHeigth;

    setLotData(tmp);
  }, [lot]);

  const getAdditional = () => {
    if (!lot || !Array.isArray(lot?.composition)) return <p></p>;

    const additional = lot.composition.map((item, i) => {
      const v = item?.variety ? getLabel(`lotVarieties_${item.variety}`) : "";
      const y = item?.year ? ` [${item.year}]` : "";
      const o = item?.origin ? `, ${item.origin}` : "";
      const p =
        lot.composition.length > 1 && item?.percentage != null
          ? ` (${item.percentage}%)`
          : "";
      return `${v}${p}${o}${y}`;
    });

    return (
      <div
        className={`additional ${isMobile ? "smaller" : ""} ${
          isName ? "only_name" : ""
        }`}
      >
        {additional.map((a, i) => (
          <p key={`additional_${i}`}>{a}</p>
        ))}
      </div>
    );
  };

  return (
    <div className={`lot_wrapper ${!isRecap ? "" : "recap"}`}>
      {isName && (
        <div
          className={`lot_name ${!isRecap ? "" : "recap"} ${
            !isDetail ? "" : "detail"
          }`}
        >
          {lotData?.name || ""}
          {isAdditional && isMobile && getAdditional()}
        </div>
      )}
      {!isDestemming && (
        <>
          <div className="init_status">
            <div className="tank_header">
              <p>{getLabel("operationDetailBefore")}</p>
            </div>
            <div className="lot_representation">
              {/* Secondary level layer (e.g., more wine programmed) */}
              {lot?.is_planned && (
                <div
                  className={`lot_estimated_progress ${getWineColorClassName(
                    lot?.color
                  )}`}
                  style={{
                    borderRadius: 0,
                    bottom: lotData?.preEstimatedBottom,
                    height: lotData?.preEstimatedHeigth,
                  }}
                ></div>
              )}
              <div
                className={"lot_progress_" + getWineColorClassName(lotData?.color)}
                style={{
                  height: lotData?.preHeigth,
                }}
              ></div>
            </div>
            <div className={!isRecap ? "lot_quantity" : ""}>
              {lotData?.old_quantity} {lotData?.old_occupancy}
            </div>
          </div>
          <div className="arrow">
            {isMobile && (
              <div className="fake-line-wrapper">
                <div className="fake-line"></div>
              </div>
            )}
            {`-${Number(lotData?.arrow_quantity)} ${isNewLot ? "L" : "Kg"}`}
            <Player
              autoplay={false}
              controls={false}
              hover={false}
              src={json_arrow_black}
              style={{
                width: "24px",
                height: "24px",
                transform: "rotate(180deg)",
              }}
            />
          </div>
        </>
      )}
      <div className={`end_status ${isRecap ? "unset-width" : ""}`}>
        {!isRecap && (
          <div className="tank_header">
            <p>{getLabel("operationDetailAfter")}</p>
          </div>
        )}
        {isDestemming && <h6 className="bold centered">{lot?.label}</h6>}
        <div className="lot_representation">
          {/* Secondary level layer (e.g., more wine programmed) */}
          {lot?.is_planned && (
            <div
              className={`lot_estimated_progress ${getWineColorClassName(lot?.color)}`}
              style={{
                borderRadius: 0,
                bottom: lotData?.postEstimatedBottom,
                height: lotData?.postEstimatedHeight,
              }}
            ></div>
          )}
          <div
            className={"lot_progress_" + getWineColorClassName(lot?.color)}
            style={{
              height: lotData?.postHeigth,
            }}
          ></div>
        </div>
        <div className="lot_quantity">
          {lotData?.new_quantity} {lotData?.new_occupancy}
          {isRecap && toRemove > 0 && (
            <h6 className="bold">{`-${Number(toRemove) || 0} ${
              isNewLot ? "L" : "Kg"
            }`}</h6>
          )}
        </div>
      </div>
      {isAdditional && !isMobile && getAdditional()}
    </div>
  );
};

export const DestBottlingDetail = ({
  bottleLot,
  capLot,
  batches = [],
  srcBatches = [],
}) => {
  const [getLabel] = useLabels();

  return (
    <div className="bottling">
      <div className="box_icons">
        {batches?.map((batch, index) => {
          const srcBatch =
            srcBatches?.find((b) => b?.batch_id === batch?.batch_id)
              ?.preOperation?.batch || {};
          return (
            <div key={`batch-${index}`} className="box_item">
              <p className="batch_name">{batch?.new_batch_name}</p>
              <Icon                 icon="fa-wine-bottle"
                className="bottle_icon"
                style={{ color: getWineColor(srcBatch?.color, "rgb") }}
              />
              <Icon                 icon="fa-box"
                style={{ color: "rgb(157, 126, 104)" }}
                size={"4x"}
              />
            </div>
          );
        })}
      </div>
      <div className="icons">
        <div className="cap_icon">
          <Icon             icon="fa-bread-slice"
            style={{ color: "rgb(157, 126, 104)" }}
            size={"sm"}
          />
          <p className="line"></p>
          <p className="bold-p">
            {getLabel("capLot")}
            <span className="value-p">{capLot || "—"}</span>
          </p>
        </div>

        <div className="bottle_icon">
          <Icon             icon="fa-wine-bottle"
            className="icon_rotate"
            style={{
              color: getWineColor(srcBatches?.[0]?.preOperation?.batch?.color, "rgb"),
            }}
            size={"4x"}
          />
          <p className="line"></p>
          <p className="bold-p">
            {getLabel("bottleLot")}
            <span className="value-p">{bottleLot || "—"}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export const DestTankDetail = ({
  tank,
  color = null,
  operation,
  isName = true,
  isDrop = true,
  isFirst = false,
  isLotDetail = false,
  isCurrentStateOnly = false,
  isMobile = false,
  isShowComposition = true,
}) => {
  const { auth } = useAuth();
  const role = auth?.role || ROLES.GUEST;
  const [getLabel] = useLabels();
  const [lotInfo, setLotInfo] = useState([]);
  const [preLotInfo, setPreLotInfo] = useState([]);
  const [hoverSection, setHoverSection] = useState(null); // 'init', 'end', or null
  const [tankData, setTankData] = useState({});

  const hoverTimeout = useRef(null);

  useEffect(() => {
    return () => clearTimeout(hoverTimeout.current);
  }, []);

  const handleMouseEnter = (isEnd = false) => {
    if (
      role !== ROLES.CLIENT ||
      isMobile ||
      !isShowComposition
    ) {
      return;
    }

    // Convert event to boolean (when called from onMouseEnter, isEnd is the event object)
    const isEndBool = typeof isEnd === 'boolean' ? isEnd : false;

    hoverTimeout.current = setTimeout(() => {
      setHoverSection(isEndBool ? "end" : "init");
    }, 120); // tweak: 80–200ms
  };

  const handleMouseLeave = () => {
    if (
      role !== ROLES.CLIENT ||
      isMobile ||
      !isShowComposition
    ) return;

    clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => {
      setHoverSection(null);
    }, 120);
  };

  const handleCompositionMouseEnter = () => {
    // Cancel any pending hide timeout when mouse enters the composition tooltip
    clearTimeout(hoverTimeout.current);
  };

  const handleCompositionMouseLeave = () => {
    // Hide the tooltip when mouse leaves the composition
    if (
      role !== ROLES.CLIENT ||
      isMobile ||
      !isShowComposition
    ) return;

    clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => {
      setHoverSection(null);
    }, 120);
  };

  const { menuPos, targetRef, closeMenu } = useContextMenu({
    isMobile,
    longPressMs: 600,
  });

  const {
    menuPos: menuPosEnd,
    targetRef: targetRefEnd,
    closeMenu: closeMenuEnd,
  } = useContextMenu({
    isMobile,
    longPressMs: 600,
  });

  const tank_id = tank?.id || tank?.tank_id;
  const items = [
    { label: getLabel("goToTankDetail"), path: `/tanks/${tank_id}` },
    {
      label: getLabel("goToTankTracking"),
      path: `/tanks/${tank_id}/tracking`,
    },
    { label: getLabel("goToTankUpdate"), path: `/tanks/${tank_id}/update` },
  ];

  useEffect(() => {
    const tmp = {};

    handleBlend();

    const oldQty = Number(tank?.old_quantity) || 0;
    const plannedQty = Number(tank?.planned_quantity) || 0;
    const arrowQty = Number(tank?.arrow_quantity) || 0;
    const dropQty =
      operation?.completion_date === null ? Number(tank?.drop) || 0 : 0;
    const tankCapacity = Number(tank?.capacity) || 1;
    const currentQty = Number(tank?.quantity) || 0;

    const estimatedPreHeight = (plannedQty / tankCapacity) * 100;
    const realPreHeight = (oldQty / tankCapacity) * 100;
    const bottomPre = ((oldQty - dropQty) / tankCapacity) * 100;

    const realPostHeight = ((oldQty - arrowQty) / tankCapacity) * 100;

    if (tank?.is_src) {
      tmp.preEstimatedHeight = `${estimatedPreHeight}%`; // Planned
      tmp.preRealHeight = `${realPreHeight}%`; // Old quantity
      tmp.preBottom = `${bottomPre}%`;

      tmp.preEstimatedQuantity = `${oldQty} / ${tankCapacity} L`;
      tmp.preEstimatedOccupancy = ` (${Math.round(
        (oldQty * 100) / tankCapacity
      )}%)`;

      tmp.postEstimatedQuantity = `${oldQty - arrowQty} / ${tankCapacity} L`;
      tmp.postEstimatedOccupancy = ` (${Math.round(
        ((oldQty - arrowQty) * 100) / tankCapacity
      )}%)`;
      tmp.postEstimatedHeight = `${Math.round(
        ((plannedQty - oldQty) * 100) / tankCapacity
      )}%`;
      tmp.postRealHeight = `${realPostHeight}%`;
      tmp.postBottom = `${
        Math.round((oldQty - arrowQty) * 100) / tankCapacity
      }%`;

      tmp.postBottomP = `${
        parseFloat(tmp.postBottom) + parseFloat(tmp.postEstimatedHeight) / 2
      }%`;
      // console.log('postEstimatedHeight', tmp.postEstimatedHeight, 'postBottom', tmp.postBottom)
    } else {
      tmp.preEstimatedHeight = `${estimatedPreHeight + realPreHeight}%`;
      tmp.preRealHeight = `${realPreHeight}%`;
      tmp.preBottom = `${bottomPre}%`;

      tmp.preEstimatedQuantity = `${oldQty + plannedQty} / ${tankCapacity} L`;
      tmp.preEstimatedOccupancy = `(${Math.round(
        ((oldQty + plannedQty) * 100) / tankCapacity
      )}%)`;

      tmp.postEstimatedQuantity = `${
        currentQty + plannedQty - dropQty
      } / ${tankCapacity} L`;
      tmp.postEstimatedOccupancy = ` (${Math.round(
        ((currentQty + plannedQty - dropQty) * 100) / tankCapacity
      )}%)`;

      tmp.postRealHeight = `${((currentQty - dropQty) / tankCapacity) * 100}%`;
      tmp.postEstimatedHeight = `${estimatedPreHeight}%`;
      tmp.postBottom = tmp.postRealHeight;
      tmp.postBottomP = `${
        ((currentQty - dropQty) / tankCapacity) * 100 + estimatedPreHeight * 0.5
      }%`;
    }
    setTankData(tmp);
  }, [tank]);

  const handleBlend = () => {
    if (Array.isArray(tank?.composition)) {
      if (tank?.is_src) {
        setLotInfo(roundPercentages(tank?.composition));
        setPreLotInfo(roundPercentages(tank?.composition));
      } else if (!tank?.is_src) {
        if (operation?.completion_date === null) {
          setPreLotInfo(roundPercentages(tank?.composition));

          setLotInfo(
            aggregateBlend()?.sort((a, b) => b?.percentage - a?.percentage)
          );
        } else {
          if (
            Array.isArray(tank?.prev_composition) &&
            tank?.prev_composition?.length > 0
          ) {
            setPreLotInfo(roundPercentages(tank?.prev_composition));
          }
          setLotInfo(roundPercentages(tank?.composition));
        }
      }
    }
  };

  const aggregateBlend = () => {
    const oldQty = parseFloat(tank?.old_quantity) || 0;
    const newQty = Number(tank?.arrow_quantity) || 0;
    const totalVolume = oldQty + newQty;

    const contributions = {};

    // Process destination's existing wine (old composition)
    if (Array.isArray(tank?.composition)) {
      tank.composition.forEach((comp) => {
        const { variety, year, origin, percentage } = comp;
        const key = `${variety}|${year}|${origin}`;
        const contribution = oldQty * (percentage / 100);

        contributions[key] = (contributions[key] || 0) + contribution;
      });
    }

    // Process sources' contributions (srcs_composition includes quantity)
    if (
      Array.isArray(tank?.srcs_composition) &&
      !(tank?.is_src && operation?.completion_date !== null)
    ) {
      tank.srcs_composition.forEach((comp) => {
        const { variety, year, origin, percentage } = comp;
        const key = `${variety}|${year}|${origin}`;
        const contribution = (newQty || 0) * (percentage / 100);
        contributions[key] = (contributions[key] || 0) + contribution;
      });
    }

    // Convert contributions to final percentages
    const result = [];
    for (const key in contributions) {
      const [variety, yearStr, origin] = key.split("|");
      const contribution = contributions[key];
      const percentage = (contribution / totalVolume) * 100;
      result.push({
        variety,
        year: Number(yearStr),
        origin,
        percentage,
      });
    }

    return roundPercentages(result) || [];
  };

  // Assume aggregated is an array of objects like:
  // [{ variety: "sangiovese", origin: "valle maggiore", year: 2024, percentage: 18.18 }, ...]
  const roundPercentages = (aggregated) => {
    // Step 1: Map to get raw values, rounded value, and remainder.
    const data =
      aggregated?.map((item) => {
        const raw = item?.percentage || 0;
        const rounded = Math.floor(raw); // Round down
        const remainder = raw - rounded;
        return { ...item, raw, rounded, remainder };
      }) || [];

    if (data?.length === 0)
      return [{ varieties: [], origins: [], years: [], percentages: [] }];

    // Step 2: Sum the rounded values.
    let sumRounded = data.reduce((sum, item) => sum + item.rounded, 0);

    // Step 3: Calculate the difference to reach 100.
    let diff = 100 - sumRounded;

    // Step 4: Sort by remainder descending.
    data.sort((a, b) => b.remainder - a.remainder);

    // Step 5: Distribute the difference among the items with highest remainder.
    for (let i = 0; i < diff; i++) {
      if (!data[i]) break;
      data[i].rounded += 1;
    }

    const mapData = data?.map((item) => ({
      variety: item.variety,
      origin: item.origin,
      year: item.year,
      percentage: item.rounded,
    }));

    return mapData?.sort((a, b) => b?.percentage - a?.percentage);
  };

  const getBarrelOccupancy = () => {
    const capacity = Number(tank?.capacity);
    const barrelsNum = Number(tank?.barrels_num);
    const plannedQty = Number(tank?.planned_quantity) || 0;
    const dropQty = Number(tank?.drop) || 0;
    const currentQty = Number(tank?.quantity) || 0;
    const totalQuantity = currentQty + plannedQty - dropQty;

    if (!capacity || !barrelsNum) return null;

    const barrelVolume = capacity / barrelsNum; // liters per barrel
    const requiredBarrels = Math.ceil(totalQuantity / barrelVolume);

    if (requiredBarrels <= barrelsNum) {
      return <p>{`${requiredBarrels}/${barrelsNum}`}</p>;
    } else {
      return null;
    }
  };

  return (
    <div className={`tanks_wrapper ${isLotDetail ? "lot_detail" : ""}`}>
      {isName && (
        <div className={`tank_name  ${isFirst ? "fix-mt" : ""}`}>
          {tank?.name}
          {/* {operation?.batch_name_out && <p className="batch_name">{operation?.batch_name_out}</p>} */}
          {/* {tank?.new_batch_name && (
            <p className="batch_name">{tank?.new_batch_name}</p>
          )} */}
        </div>
      )}
      <div
        className="init_status"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {isFirst && (
          <div className="tank_header">
            <p>{getLabel("operationDetailBefore")}</p>
          </div>
        )}
        {isLotDetail && <div className="tank_name">{tank?.name}</div>}
        {/* {false && !tank?.is_src && String(tank?.planned_batch_name)?.length === 0 && tank?.old_batch_name && (
            <p className="batch_name">{tank?.old_batch_name}</p>
          )} */}

        {(!tank?.is_planned ||
          (!tank?.is_src &&
            tank?.is_planned &&
            Number(tank?.old_quantity) === 0 &&
            Number(tank?.planned_quantity) === 0) ||
          tank?.is_src) &&
          (Number(tank?.old_quantity) === 0 || tank?.old_batch_name) && (
            <p className={`batch_name ${isCurrentStateOnly ? "bold" : ""}`}>
              {Number(tank?.old_quantity) === 0
                ? getLabel("tankState_EMPTY")
                : tank?.old_batch_name}
            </p>
          )}
        {(tank?.is_planned &&
          tank?.planned_batch_name &&
          !tank?.is_src &&
          Number(tank?.planned_quantity) > 0) ? (
            <p className="batch_name">
              {tank?.planned_batch_name || tank?.batch_name}
            </p>
          ) : <p className="batch_name bold">{tank?.is_src && !tank?.old_batch_name && "\u00A0"}</p>}
        <span className="batch_name">
          {Number(tank?.old_quantity) > 0 &&
          [0, 1, 2].includes(tank?.old_batch_type ?? tank?.batch_type) &&
          ["WINE", "BOTTLED", "MUST"].includes(tank?.state)
            ? getLabel(`lotType${tank?.old_batch_type ?? tank?.batch_type}`)
            : "\u00A0"}
        </span>
        <div
          ref={targetRef}
          className={`tank_representation ${
            tank?.type === "TONNEAU"
              ? "tonneau"
              : tank?.type === "BARRIQUE"
              ? "barrique"
              : ""
          }`}
        >
          <ContextMenu pos={menuPos} items={items} onClose={closeMenu} />
          {/* Secondary level layer (e.g., more wine programmed) */}
          {tank?.is_planned &&
            (tank?.is_src ||
              (!tank?.is_src && Number(tank?.planned_quantity) > 0)) && (
              <div
                className={`tank_tile_estimated_progress ${getWineColorClassName(
                  color || tank?.color
                )} ${
                  [
                    "STABILIZATION_STATIC",
                    "STABILIZATION_DYNAMIC",
                    "STABILIZATION_RESIN",
                  ].includes(operation?.type) && "no_bg_image"
                }`}
                style={{ height: tankData?.preEstimatedHeight }}
              ></div>
            )}
          <div
            className={
              "tank_tile_progress_" + getWineColorClassName(color || tank?.color)
            }
            style={{
              height: tankData?.preRealHeight,
            }}
          ></div>
        </div>
        <div>
          {tankData?.preEstimatedQuantity} {tankData?.preEstimatedOccupancy}
        </div>
      </div>
      {!isCurrentStateOnly && (
        <div className={`arrow ${isFirst ? "fix-mt" : ""}`}>
          {isMobile && (
            <div className="fake-line-wrapper">
              <div className="fake-line"></div>
            </div>
          )}
          {Number(tank?.arrow_quantity) !== 0 && (
            <span>
              {`${tank?.is_src ? "-" : "+"}${Number(
                tank?.arrow_quantity || 0
              )} L`}
            </span>
          )}
          {/* {operation?.type === 'DESTEMMING' ? 'KG' : 'L'} */}
          <Player
            autoplay={false}
            controls={false}
            hover={false}
            src={json_arrow_black}
            style={{
              width: "24px",
              height: "24px",
              transform: "rotate(180deg)",
            }}
          />
        </div>
      )}
      {!isCurrentStateOnly && (
        <div
          className="end_status"
          onMouseEnter={() => handleMouseEnter(true)}
          onMouseLeave={handleMouseLeave}
        >
          {/* <div className="end_status"> */}
          {isFirst && (
            <div className="tank_header">
              <p>{getLabel("operationDetailAfter")}</p>
            </div>
          )}
          {!tank?.batch_name && <p className="batch_name bold">&nbsp;</p>}
          {tank?.batch_name && (
            <p className="batch_name bold">
              {tank?.is_planned &&
                !tank?.is_src &&
                Number(tank?.planned_quantity) > 0 &&
                tank?.planned_batch_name}
              {/* {(!tank?.is_src && !tank?.is_planned && tank?.batch_name && !tank?.planned_batch_name) && tank?.batch_name} */}
              {tank?.is_planned &&
              Number(tank?.planned_quantity) > 0 &&
              !tank?.is_src
                ? ""
                : Number(tank?.quantity) === 0 ||
                  (!operation && tank?.is_src && Number(tank?.old_quantity) > 0 && Number(tank?.arrow_quantity) === Number(tank?.old_quantity))
                ? getLabel("tankState_EMPTY")
                : tank?.batch_name}
              {"is_new" in tank && (
                <span className="new">
                  {tank?.is_new ? getLabel("new") : ""}
                </span>
              )}
            </p>
          )}
          {/* {(tank?.batch_name === undefined || String(tank?.batch_name)?.length === 0 || (['WINE', 'BOTTLED'].includes(tank?.state) && Number(tank?.old_quantity) > 0)) && //(tank?.arrow_quantity === 0 || String(tank?.old_batch_name)?.length > 0) && 
        (
          <p className="no_name">&nbsp;</p>
        )} */}
          {/* {((["WINE", "BOTTLED"].includes(tank?.state) || (tank?.state === null && operation?.type === "RACKING")) 
        && [0, 1].includes(tank?.batch_type)) && <p className="no_name">&nbsp;</p>} */}
          {/* {operation?.type === "RACKING" &&
            tank?.state === null &&
            Number(tank?.old_quantity) > 0 &&
            !tank?.is_src &&
            [0, 1].includes(tank?.batch_type) &&
            ["WINE", "BOTTLED"].includes(tank?.state) && (
              <p className="no_name">&nbsp;</p>
            )} */}
          <span className="batch_name">
            {/* {Number(tank?.quantity) > 0 &&
            ["WINE", "BOTTLED"].includes(tank?.state) &&
            [0, 1, 2].includes(tank?.batch_type)
              ? getLabel(`lotType${tank?.batch_type}`)
              : "\u00A0"} */}
            {Number(tank?.quantity) > 0 && !(!operation && tank?.is_src && Number(tank?.arrow_quantity) === Number(tank?.old_quantity)) ? (
              ["WINE", "BOTTLED", "MUST"].includes(tank?.state) &&
              [0, 1, 2].includes(tank?.batch_type)
                ? getLabel(`lotType${tank?.batch_type}`)
                : [0, 1, 2].includes(operation?.batch_type) && tank?.batch_name
                ? getLabel(`lotType${operation?.batch_type}`)
                : "\u00A0"
            ) : "\u00A0"}
          </span>

          {(tank?.type === "TONNEAU" || tank?.type === "BARRIQUE") && (
            <div
              className={`barrel_occupancy ${
                tank?.type === "TONNEAU" ? "tonneau" : ""
              } ${isFirst ? "fix_top" : ""}`}
            >
              {getBarrelOccupancy()}
            </div>
          )}
          {/* {!(!(Number(tank?.planned_quantity) > 0 && !tank?.is_src) && Number(tank?.quantity) === 0) && ["FLOWER", "PRESSED"].includes(tank?.batch_type) && (tank?.state === "WINE" || (tank?.state === null && operation?.type === "RACKING")) && <span className="batch_name">{getLabel(`lotType_${tank?.batch_type}`)}</span>} */}
          {/* {!(
            !(Number(tank?.planned_quantity) > 0 && !tank?.is_src) &&
            Number(tank?.quantity) === 0
          ) &&
            [0, 1].includes(tank?.batch_type) &&
            (tank?.state === "WINE" ||
              (tank?.state === null && operation?.type === "RACKING")) && (
              <span className="batch_name">
                {getLabel(`lotType${tank?.batch_type}`)}
              </span>
            )} */}
          <div
            ref={targetRefEnd}
            className={`tank_representation ${
              tank?.type === "TONNEAU"
                ? "tonneau"
                : tank?.type === "BARRIQUE"
                ? "barrique"
                : ""
            }`}
          >
            <ContextMenu
              pos={menuPosEnd}
              items={items}
              onClose={closeMenuEnd}
              onOpen={closeMenu}
            />
            {/* Actual wine in tank during the current operation */}
            <div
              className={`tank_tile_progress_${getWineColorClassName(
                color || tank?.color
              )}`}
              style={{
                height: tankData.postRealHeight,
              }}
            ></div>

            {/* Estimated wine after programmed operations, stacked on top */}
            {tank?.is_planned && Number(tank?.planned_quantity) > 0 && (
              <div
                className={`tank_tile_estimated_progress ${getWineColorClassName(
                  color || tank?.color
                )}`}
                style={{
                  borderRadius: "0",
                  height: tankData?.postEstimatedHeight,
                  bottom: tankData?.postBottom, // Stacks on top of current wine
                }}
              ></div>
            )}

            {/* Floating text centered inside tank_tile_estimated_progress */}
            {tank?.is_planned &&
              (tank?.is_src || Number(tank?.planned_quantity) > 0) && (
                <p
                  className="floating_estimated_text"
                  style={{
                    // height: "auto", // Let content dictate height
                    height: tankData.postEstimatedHeight,
                    bottom: tankData.postBottomP, // Positioned in the middle of estimated progress
                  }}
                >
                  {tank?.is_src
                    ? `${Math.abs(
                        Number(tank?.old_quantity) -
                          Number(tank?.planned_quantity)
                      )} L`
                    : `${Number(tank?.planned_quantity)} L`}
                </p>
              )}
          </div>
          {/* <div>
          {(Number(tank?.quantity) - Number(tank?.drop || 0)) || 0} / {tank?.capacity}
          {"L"} (
          {Math.round(
            (((Number(tank?.quantity) - Number(tank?.drop || 0)) || 0) * 100) / Number(tank?.capacity)
          )}
          %)
        </div> */}
          <div className="planned_total_quantity">
            {tankData?.postEstimatedQuantity} {tankData?.postEstimatedOccupancy}
          </div>
        </div>
      )}
      {isDrop && operation?.completion_date !== null && (
        <div className="operation_info_container_row">
          <p className={`tank_name ${isFirst ? "fix-mt" : ""}`}>
            {getLabel("operationDrop")}
            <span className="batch_name">
              {" "}
              {`${tank?.drop || 0}`}{" "}
              {operation?.type === "DESTEMMING" ? "Kg" : "L"}
            </span>
          </p>
        </div>
      )}
      {[
        "STABILIZATION_STATIC",
        "STABILIZATION_DYNAMIC",
        "STABILIZATION_RESIN",
      ].includes(operation?.type) &&
        tank?.percentage && (
          <p className="stabilization_additional">
            {tank?.percentage} %{" "}
            <span>
              {getLabel("ofPercentage")} {tank?.planned_quantity} L
            </span>
          </p>
        )}
      {!isMobile &&
        isShowComposition &&
        (() => {
          const isEndBlocked =
            hoverSection === "end" &&
            tank?.is_src &&
            Number(tank?.quantity) <= 0 &&
            lotInfo?.length > 0;

          const isInitBlocked =
            hoverSection === "init" &&
            !tank?.is_src &&
            Number(tank?.old_quantity) <= 0 &&
            preLotInfo?.length === 0;

          if (isEndBlocked || isInitBlocked) return null;

          return (
            <div
              className="composition_wrapper"
              onMouseEnter={handleCompositionMouseEnter}
              onMouseLeave={handleCompositionMouseLeave}
            >
              <TankCompositionDetail
                isLotDetail={isLotDetail}
                isFirst={isFirst}
                setHoverSection={setHoverSection}
                hoverSection={hoverSection}
                preLotInfo={preLotInfo}
                lotInfo={lotInfo}
                tank={tank}
                role={role}
                isMobile={isMobile}
              />
            </div>
          );
        })()}
    </div>
  );
};

export const TankCompositionDetail = ({
  setHoverSection = () => null,
  hoverSection,
  preLotInfo = [],
  lotInfo = [],
  tank,
  role = ROLES.GUEST,
  isFirst = false,
  isLotDetail = false,
  isMobile = false,
}) => {
  // Check if composition data is empty (roundPercentages returns [{varieties:[], ...}] when empty)
  const hasPreLotInfo = preLotInfo?.some((item) => item?.variety);
  const hasLotInfo = lotInfo?.some((item) => item?.variety);

  if (
    (hoverSection === "init" && !hasPreLotInfo) ||
    (hoverSection === "end" && !hasLotInfo)
  )
    return;

  return (
    <div className="composition">
      {role === ROLES.CLIENT && (
        (hoverSection === "init" && preLotInfo?.length > 0) ||
        (hoverSection === "end" && lotInfo?.length > 0)
      ) && (
        <div
          className={`lot_info_wrapper ${hoverSection ? "visible" : ""} ${
            isFirst ? "fix-mt" : ""
          } ${isLotDetail ? "lot_detail" : ""}`}
        >
          {isMobile && hoverSection && (
            <Icon               onClick={() => setHoverSection("init")}
              icon="fa-angles-right"
              className={`lot_arrow ${hoverSection ? "open" : ""}`}
            />
          )}
          {hoverSection && (
            <ul className="lot_info">
              {(hoverSection === "init"
                ? preLotInfo
                : lotInfo
              )?.map((i, index) => {
                return (
                  <li
                    className="batch_name"
                    key={`varieties-${tank?.name}-${index}`}
                  >
                    {i?.variety || ""}
                    {i?.year ? ` [${i?.year}]` : ""}
                    {i?.origin ? `, ${i?.origin}` : ""}
                    {i?.percentage || "0" ? ` (${i?.percentage}%)` : ""}
                  </li>
                );
              }, "") || ""}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export const SrcTankDetail = ({
  tank,
  lot,
  isCurrentStateOnly = false,
  totalDrug,
  isTankNameUp = false,
}) => {
  const [getLabel] = useLabels();
  let wineColor = "red";
  switch (lot?.color || tank?.color) {
    case "WHITE":
      wineColor = "white";
      break;
    case "ROSE":
      wineColor = "rose";
      break;
    case "ORANGE":
      wineColor = "orange";
      break;
    default:
      wineColor = "red";
      break;
  }

  const convertToHL = () => {
    const quantity = Number(tank?.old_quantity) || 0;
    const capacity = Number(tank?.capacity);
    const isLargeTank = capacity >= 10000;

    const displayedQuantity = parseInt(
      isLargeTank ? (quantity / 100).toFixed(1) : quantity
    );
    const displayedCapacity = parseInt(
      isLargeTank ? (capacity / 100).toFixed(0) : capacity
    );
    const unit = isLargeTank ? "hL" : "L";

    const percentage =
      capacity > 0 ? Math.round((quantity * 100) / capacity) : 0;

    return (
      <div>
        {displayedQuantity || 0} / {displayedCapacity} {unit}
        {!isTankNameUp ? " (" + percentage + "%)" : ""}
        {isTankNameUp && (
          <div>
            {" (" +
              Math.round((displayedQuantity * 100) / displayedCapacity) +
              "%)"}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="tanks_wrapper">
      <div className="tank_name">
        {!isTankNameUp && <p>{tank?.name}</p>}
        {/* {tank?.batch_name && <p className="batch_name">{Number(tank?.quantity) > 0 ? tank?.batch_name || getLabel("tankState_EMPTY") : getLabel("tankState_EMPTY")}</p>} */}
        {/* <div>{operation?.batch_name_out && <div className="batch_name">{operation?.batch_name_out}</div>}</div> */}
      </div>
      <div className="init_wrapper">
        <div
          className={`init_status ${
            ["TONNEAU", "BARRIQUE"].includes(tank?.type) ? "barrel" : ""
          } ${tank?.type === "TONNEAU" ? "tonneau" : ""}`}
        >
          {tank?.old_batch_name && (
            <p className={`batch_name ${isCurrentStateOnly ? "bold" : ""}`}>
              {Number(tank?.old_quantity) > 0
                ? tank?.old_batch_name || getLabel("tankState_EMPTY")
                : getLabel("tankState_EMPTY")}
            </p>
          )}
          {isTankNameUp && <p>{tank?.name}</p>}
          {[0, 1, 2].includes(tank?.batch_type) && ["WINE", "MUST"].includes(tank?.state) && (
            <span className="batch_name">
              {getLabel(`lotType${tank?.batch_type}`)}
            </span>
          )}
          <div
            className={`tank_representation ${
              ["TONNEAU", "BARRIQUE"].includes(tank?.type) ? "barrel" : ""
            } ${tank?.type === "TONNEAU" ? "tonneau" : ""}`}
          >
            {/* Secondary level layer (e.g., more wine programmed) */}
            {isCurrentStateOnly &&
              tank?.planned_quantity &&
              tank?.planned_quantity !== tank?.old_quantity && (
                <div
                  className={`tank_tile_estimated_progress ${wineColor}`}
                  style={{ height: `${tank?.planned_quantity}%` }}
                ></div>
              )}
            <div
              className={"tank_tile_progress_" + wineColor}
              style={{
                height: `${
                  (Number(tank?.old_quantity) / Number(tank?.capacity)) * 100
                }%`,
              }}
            ></div>
          </div>
          <div>
            {/* {tank?.old_quantity || 0} / {tank?.capacity}L
            {!isTankNameUp ? " (" + Math.round(
              ((Number(tank?.old_quantity) || 0) * 100) /
                Number(tank?.capacity)
            ) + '%)' : ''} */}
            {convertToHL()}
          </div>
          {/* {isTankNameUp && <div>
            {" (" + Math.round(
              ((Number(tank?.old_quantity) || 0) * 100) /
                Number(tank?.capacity)
            ) +
            "%)"}
          </div>} */}
        </div>
        {totalDrug}
      </div>
      {!isCurrentStateOnly && (
        <>
          <div className="arrow">
            {`-${Number(tank?.arrow_quantity)} L`}
            <Player
              autoplay={false}
              controls={false}
              hover={false}
              src={json_arrow_black}
              style={{
                width: "24px",
                height: "24px",
                transform: "rotate(180deg)",
              }}
            />
          </div>
          <div
            className={`end_status ${
              ["TONNEAU", "BARRIQUE"].includes(tank?.type) ? "barrel" : ""
            } ${tank?.type === "TONNEAU" ? "tonneau" : ""}`}
          >
            {tank?.batch_name && (
              <p className="batch_name bold">
                {Number(tank?.quantity) || 0 + Number(tank?.toAdd) || 0 > 0
                  ? tank?.batch_name || getLabel("tankState_EMPTY")
                  : getLabel("tankState_EMPTY")}
              </p>
            )}
            <div
              className={`tank_representation ${
                ["TONNEAU", "BARRIQUE"].includes(tank?.type) ? "barrel" : ""
              } ${tank?.type === "TONNEAU" ? "tonneau" : ""}`}
            >
              {!isCurrentStateOnly &&
                Number(tank?.planned_quantity) > 0 &&
                tank?.planned_quantity !== tank?.old_quantity && (
                  <div
                    className={`tank_tile_estimated_progress ${wineColor}`}
                    style={{ height: `${tank?.planned_quantity}%` }}
                  ></div>
                )}
              <div
                className={"tank_tile_progress_" + wineColor}
                style={{
                  height: `${
                    ((Number(tank?.quantity) || 0) / Number(tank?.capacity)) *
                    100
                  }%`,
                }}
              ></div>
            </div>
            <div>
              {Number(tank?.quantity) || 0} / {tank?.capacity}
              {"L"} (
              {Math.round(
                ((Number(tank?.quantity) || 0) * 100) / Number(tank?.capacity)
              )}
              %)
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const BatchDetail = ({ lot }) => {
  const [getLabel] = useLabels();

  return (
    <>
      <Label label={getLabel("lot")}>{lot?.name}</Label>
    </>
  );
};

export const DestemmingDetail = ({
  operation,
  users,
  tanks,
  lots,
  configs,
  // batchData,
  register,
  completed,
  drops,
  errors,
  treatments = [],
  getValues,
  setValue,
  axiosPrivate,
  dispatch,
  user_id,
  isNewLot = false,
  userSockets,
  isMobile,
  handleOpenModal,
  watchedDrugBatches,
  virtualTanks = null,
  isVirtualMode = false,
  isNextToExecute = false,
  protocolApplicationBanner = null,
}) => {
  const [getLabel] = useLabels();
  const { auth } = useAuth();
  const role = auth?.role || ROLES.GUEST;
  const [drugsData, setDrugsData] = useState([]);
  const [isShowCost, setIsShowCost] = useState(false);

  // const [completedState, setCompletedState] = useState([]);
  
  const treatmentColumns = getTreatmentColumns("TREATMENT", auth, role, operation, isShowCost, register, users, watchedDrugBatches, getLabel);

  useEffect(() => {
    if (operation?.treatments?.length > 0 && (operation?.src_tanks?.[0]?.color === "WHITE" || operation?.subtype)) {
      const tmp = computeTreatmentsData("DESTEMMING", operation, treatments);
      setDrugsData(tmp);
    }
  }, [operation, treatments]);

  const getTotalQuantity = () => {
    return (
      (operation?.dest_tanks || [])?.reduce((acc, curr) => {
        return acc + (Number(curr?.arrow_quantity) || 0);
      }, 0) || 0
    );
  };

  const getLotData = (actualLot) => {
    const lot = lots?.find((l) => l?.id == actualLot?.batch_id) || {};

    if (operation?.completion_date) {
      const pomaces_quantity =
        (Number(operation?.pomaces?.[0]?.quantity) || 0) + (Number(operation?.pomaces?.[1]?.quantity) || 0);
      // const activePlannedLotQuantity = lot?.status === "CONFIRMED" && lot?.is_planned && Number(lot?.actual_quantity) > 0?
      //   (Number(lot?.actual_quantity) - Number(actualLot?.arrow_quantity)) : 0;
      // actualLot.quantity = actualLot?.initial_quantity - actualLot?.arrow_quantity;
      return {
        name: actualLot?.batch_name,
        color: actualLot?.color,
        initial_quantity: actualLot?.initial_quantity,
        composition: actualLot?.composition,
        // quantity: Number(actualLot?.quantity) + Number(actualLot?.arrow_quantity),
        // real_initial_quantity: Number(actualLot?.quantity) + Number(actualLot?.arrow_quantity) - activePlannedLotQuantity,
        // real_final_quantity: Number(actualLot?.quantity),
        real_initial_quantity: Number(actualLot?.old_quantity),
        real_final_quantity: Number(actualLot?.quantity) - pomaces_quantity,
        arrow_quantity: actualLot?.arrow_quantity + pomaces_quantity,
        // estimated_initial_quantity: activePlannedLotQuantity,
        // estimated_final_quantity: activePlannedLotQuantity,// - Number(actualLot?.arrow_quantity),
        // is_planned: lot?.status === "CONFIRMED" && lot?.is_planned,
      };
      // return {
      //   name: actualLot?.name,
      //   color: actualLot?.color,
      //   initial_quantity: actualLot?.initial_quantity,
      //   quantity: Number(actualLot?.quantity) + Number(actualLot?.arrow_quantity),
      //   real_initial_quantity: Number(actualLot?.quantity) + Number(actualLot?.arrow_quantity) - activePlannedLotQuantity,
      //   real_final_quantity: Number(actualLot?.quantity) - activePlannedLotQuantity,
      //   arrow_quantity: actualLot?.arrow_quantity,
      //   estimated_initial_quantity: activePlannedLotQuantity,
      //   estimated_final_quantity: activePlannedLotQuantity,// - Number(actualLot?.arrow_quantity),
      //   is_planned: lot?.status === "CONFIRMED" && lot?.is_planned,
      //   varieties: lot?.varieties,
      //   origins: lot?.origins,
      //   years: lot?.years,
      // };
    }
    const pomaces = Number(operation?.pomaces?.[0]?.quantity) || 0;
    const pomacesW = Number(operation?.pomaces?.[1]?.quantity) || 0;
    const pomacesQuantity = pomaces + pomacesW;
    const isPlannedByOtherOps = lot?.shared_operation_ids?.some(
      (p_id) => p_id !== Number(operation?.id)
    );
    const plannedPomacesOtherOps =
      lot?.is_planned && isPlannedByOtherOps
        ? lot?.planned_pomaces
            ?.filter((t) =>
              operation?.id ? t?.operation_id !== operation?.id : true
            )
            ?.reduce((acc, curr) => acc + (Number(curr?.quantity) || 0), 0) || 0
        : 0;
    const allocatedInitialQuantity = getTotalQuantity();

    // Calculate the quantity available for consumption in the current operation:
    // This is the difference between the real (current) quantity and what remains planned after scheduled operations.

    // Get the total allocated quantity across destination tanks (dynamic value)
    const allocated = getTotalQuantity();
    const availableForCurrentOp =
      allocatedInitialQuantity + (Number(lot?.planned_quantity) || 0);

    // The current operation can only remove as much as is available
    const arrowQuantity = Math.min(availableForCurrentOp, allocated);

    // Build the lotData object
    const lotData = {};
    lotData.name = lot?.name || lot?.label || "";
    lotData.color = lot?.color;
    lotData.initial_quantity = lot?.quantity;

    // Set arrow_quantity: the amount to be removed by the current operation
    lotData.arrow_quantity = arrowQuantity + pomacesQuantity;
    // console.log('lot', lot)

    lotData.is_planned =
      lot?.is_planned &&
      lot?.shared_operation_ids?.filter((op_id) => op_id !== operation?.id)
        ?.length > 0;
    // Set the planned quantity for the current operation:
    // This represents how much will be consumed (arrowQuantity) and thus how much is 'planned' for removal.
    // (If no operation is executed, lot.quantity remains unchanged.)
    // In an editing context (when 'id' exists), we update values based on current distribution.
    if (availableForCurrentOp > 0) {
      // For display when editing:
      // - The new "quantity" (i.e. what is scheduled to remain) is the planned_quantity from the lot.
      // - The amount consumed by the current operation is arrowQuantity.
      lotData.quantity = Number(lot?.planned_quantity) || 0; // What is expected to remain after scheduled ops
      lotData.planned_quantity = arrowQuantity; // Amount to be removed by the current op

      // For additional display details:
      // "real_initial_quantity" shows the total available for consumption (availableForCurrentOp)
      // "estimated_initial_quantity" shows what remains if we subtract the arrowQuantity from the current quantity.
      lotData.real_initial_quantity =
        availableForCurrentOp - plannedPomacesOtherOps;
      lotData.estimated_initial_quantity =
        Number(lot?.quantity) - availableForCurrentOp;

      // Final quantity calculations after the current operation:
      // The real final quantity would be the current lot.quantity minus the amount removed
      lotData.real_final_quantity =
        availableForCurrentOp -
        arrowQuantity -
        pomacesQuantity -
        plannedPomacesOtherOps;
      lotData.estimated_final_quantity =
        Number(lot?.quantity) - availableForCurrentOp;
    } else {
      // If nothing is available, all values become zero.
      lotData.quantity = 0;
      lotData.planned_quantity = 0;
      lotData.real_initial_quantity = 0;
      lotData.estimated_initial_quantity = 0;
      lotData.real_final_quantity = 0;
      lotData.estimated_final_quantity = 0;
    }

    lotData.composition = lot?.composition;

    return lotData;
  };

  return (
    <>
      <h4>{getLabel("operationDetail")}</h4>
      <div className="operation_config_with_banner">
        <div className="config_section">
          <div
            className={`operation_info_container_row ${
              operation?.cellarman_ids?.length > 1 ? "start" : ""
            }`}
          >
            <Label label={getLabel("insertionBy")}>
              <div>
                {users?.find?.(
                  (user) => Number(user.id) === Number(operation?.user_id)
                )?.username || ""}
              </div>
            </Label>
            <Label label={getLabel("insertionDate")}>
              {format(
                new Date(operation?.insertion_date || null),
                configs.shortDateFormat
              )}
            </Label>
            {operation?.completion_date === null && (
              <>
                <Label label={getLabel("expireDate")}>
                  {format(
                    new Date(operation?.expire_date || null),
                    configs.shortDateFormat
                  )}
                </Label>
                <Label label={getLabel("cellarman")}>
                  {
                    <div className="cellarman_container">
                      {operation?.cellarman_ids?.map((cellarman, index) => {
                        const cellarmanName =
                          users?.find?.(
                            (user) => Number(user.id) === Number(cellarman)
                          )?.username || "";
                        return (
                          <div key={`cellarman-${index}`}>
                            {cellarmanName +
                              (operation?.cellarman_ids?.length > 1 ? " " : "")}
                          </div>
                        );
                      })}
                    </div>
                  }
                </Label>
              </>
            )}
            {operation?.completion_date && (
              <>
                <Label label={getLabel("operationCompletionUser")}>
                  <div>
                    {users?.find?.(
                      (user) => Number(user.id) === Number(operation?.completed_by)
                    )?.username || ""}
                  </div>
                </Label>
                <Label label={getLabel("operationCompletionDate")}>
                  {format(
                    new Date(operation?.completion_date || null),
                    configs.dateFormat
                  )}
                </Label>
              </>
            )}
            <Label label={getLabel("operationPriority")}>
              {getLabel(`operationPriority${operation?.priority}`)}
            </Label>
            {/* <Label label={getLabel("lotType")}>
              {operation?.batch_type ? getLabel(`lotType${operation?.batch_type}`) : ''}
            </Label> */}
          </div>
        </div>
        <div className="banner_section">
          {protocolApplicationBanner}
        </div>
      </div>
      <h4>{getLabel(isNewLot ? "fromLotExternal" : "fromLot")}</h4>
      <div className="operation_info_container_row">
        <div className="tanks_wrapper">
          {!isNewLot && (
            <DestLotDetail
              // lot={operation?.completion_date === null ? operation?.src_tanks?.[0]?.preOperation?.batch : operation?.src_tanks?.[0]?.postOperation?.batch}
              isAdditional={true}
              lot={getLotData(operation?.src_tanks?.[0])}
              operation={operation}
              isMobile={isMobile}
              isDetail={true}
            />
          )}
          {isNewLot && (
            <>
              <Label label={getLabel("lotNameExternal")}>
                {operation?.src_tanks?.[0]?.name || ""}
              </Label>
              <Label label={getLabel("quantity")}>
                {operation?.src_tanks?.[0]?.arrow_quantity || 0} L
              </Label>
            </>
          )}
        </div>
      </div>

      {isNewLot && (
        <>
          <div className="operation_info_container_row lot_info">
            {operation?.src_tanks?.[0]?.label &&
              operation?.src_tanks?.[0]?.label?.length > 0 && (
                <Label label={getLabel("lotLabel")}>
                  {operation?.src_tanks?.[0].label?.map((lab, i) => {
                    return (
                      <p key={`label-${i}`}>{getLabel(`lotLabel_${lab}`)}</p>
                    );
                  }, "") || ""}
                </Label>
              )}
            <Label label={getLabel("lotVarieties")}>
              {operation?.src_tanks?.[0]?.composition?.map((c, i) => (
                <p key={`variety-${i}`}>
                  {c.variety
                    ? `${getLabel(`lotVarieties_${c.variety}`)}${
                        operation?.src_tanks?.[0]?.composition?.length > 1
                          ? " (" + c.percentage + "%)"
                          : ""
                      }`
                    : ""}
                </p>
              )) || ""}
            </Label>

            <Label label={getLabel("lotYears")}>
              {operation?.src_tanks?.[0]?.composition?.map((c, i) => (
                <p key={`year-${i}`}>{c.year || ""}</p>
              )) || ""}
            </Label>

            <Label label={getLabel("lotOrigins")}>
              {operation?.src_tanks?.[0]?.composition?.map((c, i) => (
                <p key={`origin-${i}`}>{c.origin || ""}</p>
              )) || ""}
            </Label>
          </div>
          <div className="operation_info_container_row lot_info">
            <Label label={getLabel("lotQuality")}>
              {getLabel(`lotQuality_${operation?.src_tanks?.[0].quality}`)}
            </Label>
            {operation?.src_tanks?.[0]?.color && (
              <Label label={getLabel("lotColor")}>
                {getLabel(`lotColor_${operation?.src_tanks?.[0].color}`)}
              </Label>
            )}
          </div>
        </>
      )}
      <h4>{getLabel("toTanks")}</h4>
      <ul>
        {operation?.dest_tanks?.map((destTank, idx) => {
          return (
            <li key={`dest-${idx}`}>
              <TankOperationDetail
                type={"DESTEMMING"}
                completed={completed}
                drops={drops}
                role={role}
                users={users}
                tanks={tanks}
                lots={lots}
                tank={destTank}
                operation={operation}
                idx={idx}
                register={register}
                errors={errors}
                setValue={setValue}
                getLabel={getLabel}
                isMobile={isMobile}
                virtualTanks={virtualTanks}
                isVirtualMode={isVirtualMode}
                isNextToExecute={isNextToExecute}
                totalDrug={renderTotal(
                  tanks,
                  operation,
                  destTank?.tank_id,
                  isShowCost,
                  watchedDrugBatches,
                  getLabel
                )}
              />
            </li>
          );
        })}
      </ul>
      
      {operation?.type === "DESTEMMING" &&
        (operation?.treatments?.length > 0 && (operation?.src_tanks?.[0]?.color === "WHITE" || operation?.subtype)) && operation?.protocol_id && (
        <>
          <h4>{getLabel("operationDestemmingPressingDrugs")}</h4>
          <div className="operation_info_container_row">
            <div className="analysis_table">
              {/* <Table data={drugsData} customColumns={columns} /> */}
              <TableEdit
                defaultData={drugsData}
                columns={treatmentColumns}
                isSearchBar={false}
                type={
                  isShowCost
                    ? "treatmentApplication"
                    : role === ROLES.USER
                    ? "treatmentApplicationUser"
                    : "treatment"
                }
                isLegend={false}
                // isEditable={role === ROLES.USER}
                modalHandler={handleOpenModal}
              />
            </div>
          </div>
          
          <div className="operation_info_container_title">
            <h4>{""}</h4>
            {(role === 2 ||
              (role === ROLES.USER &&
                users?.filter((u) => u?.type === "USER")?.length === 0)) && (
              <CTA
                customClassName={!isShowCost ? "add" : "complete"}
                onClick={() => setIsShowCost(!isShowCost)}
              >
                {getLabel(
                  isShowCost
                    ? "fromTanksAnalysisHideCost"
                    : "fromTanksAnalysisShowCost"
                )}
              </CTA>
            )}
            {(role === 3 && operation?.cellarman_ids?.includes(role)) && (
              <CTA
                customClassName={!isShowCost ? "add" : "complete"}
                onClick={() => setIsShowCost(!isShowCost)}
              >
                {getLabel(
                  isShowCost
                    ? "fromTanksAnalysisHideCost"
                    : "fromTanksAnalysisShowCost"
                )}
              </CTA>
            )}
          </div>
        </>)}

      {operation?.type === "DESTEMMING" &&
        operation?.pomaces?.length > 0 && (
          <>
            <h4>{getLabel("operationStems")}</h4>
            <div className="operation_info_container_row">
              {renderPomaces(operation, getLabel)}
            </div>
          </>
        )}
    </>
  );
};

export const TreatmentDetail = ({
  type,
  operation,
  completed,
  register,
  users,
  tanks,
  lots,
  errors,
  treatments,
  configs,
  setValue,
  isMobile,
  handleOpenModal,
  watchedDrugBatches,
  virtualTanks = null,
  isVirtualMode = false,
  isNextToExecute = false,
  protocolApplicationBanner = null,
}) => {
  const [getLabel] = useLabels();
  const { auth } = useAuth();
  const role = auth?.role || ROLES.GUEST;

  // const [completedState, setCompletedState] = useState([]);

  // const setCheckboxState = (tank_id) => {
  //   const newCompletedState = [...completedState];
  //   const index = newCompletedState.indexOf(tank_id);
  //   if (index === -1) {
  //     newCompletedState.push(tank_id);
  //   } else {
  //     newCompletedState.splice(index, 1);
  //   }
  //   setCompletedState(newCompletedState);
  // };
  const isBatchTreatment = operation?.on_tank === false;
  const [drugsData, setDrugsData] = useState([]);
  const [isShowCost, setIsShowCost] = useState(false);
  
  const columns = getTreatmentColumns(type, auth, role, operation, isShowCost, register, users, watchedDrugBatches, getLabel);

  // useEffect(() => {
  //   // const tmp = computeTreatmentsData(type, operation, treatments, register, getLabel);
  //   setDrugsData(aggregateDrugsById((operation?.treatments || []))?.map(a => { return { ...a, opType: "TREATMENTS" }}));
  //   console.log(operation, 'tlen', operation?.treatments, 'tlen 2', aggregateDrugsById((operation?.treatments || []))?.map(a => { return { ...a, opType: "TREATMENTS" }}))
  //   // setDrugsData(tmp?.map(a => { return { ...a, opType: "TREATMENTS" }}));
  // }, [treatments, operation]);

  useEffect(() => {
    const tmp = computeTreatmentsData(type, operation, treatments);
    setDrugsData(tmp?.map(a => ({ ...a, opType: "TREATMENTS" })));
  }, [treatments, operation, type]);

  return (
    <>
      <h4>{getLabel("operationDetail")}</h4>
      <div className="operation_config_with_banner">
        <div className="config_section">
          <div className="operation_info_container_row">
            <Label label={getLabel("insertionBy")}>
              <div>
                {users?.find?.(
                  (user) => Number(user.id) === Number(operation?.user_id)
                )?.username || ""}
              </div>
            </Label>
            <Label
              label={
                operation?.completion_date === null
                  ? getLabel("insertionDate")
                  : getLabel("operationCompletionDate")
              }
            >
              {format(
                new Date(operation?.insertion_date || null),
                operation?.completion_date === null
                  ? configs.shortDateFormat
                  : configs.dateFormat
              )}
            </Label>
            <Label label={getLabel("expireDate")}>
              {format(
                new Date(operation?.expire_date || null),
                configs.shortDateFormat
              )}
            </Label>
            <Label label={getLabel("cellarman")}>
              {
                <div className="cellarman_container">
                  {operation?.cellarman_ids?.map((cellarman, index) => {
                    const cellarmanName =
                      users?.find?.((user) => Number(user.id) === Number(cellarman))
                        ?.username || "";
                    return (
                      <div key={`cellarman-${index}`}>
                        {cellarmanName +
                          (operation?.cellarman_ids?.length > 1 ? " " : "")}
                      </div>
                    );
                  })}
                </div>
              }
            </Label>
            <Label label={getLabel("operationPriority")}>
              {getLabel(`operationPriority${operation?.priority}`)}
            </Label>
          </div>
        </div>
        <div className="banner_section">
          {protocolApplicationBanner}
        </div>
      </div>
      <h4>
        {getLabel(
          type === "CLEANING" ? "wineMakingCIPProtocols" : "treatmentProtocols"
        )}
      </h4>
      <div className="operation_info_container_row">
        <Label label={getLabel("protocolName")}>
          {type === "CLEANING" ? treatments?.name : treatments?.protocols_name}
        </Label>
      </div>
      {/* <h3>{getLabel(`tanksInvolved`)}</h3>
        {operation?.src_tanks?.map((srcTanks) => {
            return <div className="operation_info_container_row">
                <tanksDetails
                    key={`src-${srcTanks.id}`}
                    tank={tanks.find((tank) => Number(tank.id) === Number(srcTanks?.tank_id))}
                    operation={operation}
                ></tanksDetails>
            </div>
        })} */}
      <div className="operation_info_container_row">
        <div className="analysis_table">
          {/* <Table data={drugsData} customColumns={columns} /> */}
          <TableEdit
            defaultData={drugsData}
            columns={columns}
            isSearchBar={false}
            type={
              isShowCost
                ? "treatmentApplication"
                : role === ROLES.USER
                ? "treatmentApplicationUser"
                : "treatment"
            }
            isLegend={false}
            modalHandler={handleOpenModal}
            // isEditable={role === ROLES.USER}
          />
        </div>
      </div>
      {!isBatchTreatment && (
        <>
          <div className="operation_info_container_title">
            <h4>{getLabel("fromTanksAnalysis")}</h4>
            {(role === 2 ||
              (role === ROLES.USER &&
                users?.filter((u) => u?.type === "USER")?.length === 0)) && (
              <CTA
                customClassName={!isShowCost ? "add" : "complete"}
                onClick={() => setIsShowCost(!isShowCost)}
              >
                {getLabel(
                  isShowCost
                    ? "fromTanksAnalysisHideCost"
                    : "fromTanksAnalysisShowCost"
                )}
              </CTA>
            )}
          </div>
          {operation?.src_tanks?.map((srcTank, index) => {
            return (
              <div
                className="operation_info_container_row"
                key={`tank-row-${index}`}
              >
                <TankOperationDetail
                  // type={"TREATMENT"}
                  type={type}
                  completed={completed}
                  role={role}
                  users={users}
                  tanks={tanks}
                  lots={lots}
                  totalDrug={renderTotal(
                    tanks,
                    operation,
                    srcTank?.tank_id,
                    isShowCost,
                    watchedDrugBatches,
                    getLabel
                  )}
                  tank={srcTank}
                  operation={operation}
                  idx={index}
                  register={register}
                  errors={errors}
                  setValue={setValue}
                  getLabel={getLabel}
                  isMobile={isMobile}
                  virtualTanks={virtualTanks}
                  isVirtualMode={isVirtualMode}
                  isNextToExecute={isNextToExecute}
                />
                {/* <SrcTankDetail
                  key={`src-tr-${srcTank?.tank_id}`}
                  tank={srcTank}
                  isCurrentStateOnly={true}
                  totalDrug={renderTotal(srcTank?.tank_id)}
                ></SrcTankDetail> */}
                {/* {operation?.completion_date === null &&
                  (role === 3 ||
                    (role === ROLES.CLIENT &&
                      users?.filter((u) => u?.type === "USER")?.length ===
                        0)) && (
                    <>
                      <Input
                        name={`completed.${srcTank.tank_id}`}
                        register={register}
                        error={errors[`completed.${srcTank.tank_id}`]}
                        required={getLabel("inputRequiredError")}
                        isLabel={false}
                        type={"checkbox"}
                        checked={completedState.includes(srcTank.tank_id)}
                        onChange={() => setCheckboxState(srcTank.tank_id)}
                        customClassName={"confirm"}
                      />
                    </>
                  )} */}
              </div>
            );
          })}
        </>
      )}
      {isBatchTreatment && (
        <>
          <h3>{getLabel("fromLotsAnalysis")}</h3>
          {operation?.src_tanks?.map((srcTanks, index) => {
            return (
              <div
                className="operation_info_container_row"
                key={`tank-row-${index}`}
              >
                <BatchDetail
                  key={`src-${srcTanks.id}`}
                  lot={lots.find(
                    (lot) => Number(lot.id) === Number(srcTanks?.batch_id)
                  )}
                ></BatchDetail>
              </div>
            );
          })}
        </>
      )}
      {/* <div className="operation_info_container_row">
        <Label label={getLabel("operationNotes")} width={400} height={150} customClassName={"notes"}>
          {operation?.notes}
        </Label>
      </div> */}
      {/* <div className="operation_info_container_row">
        <ChatArea
          name="chat"
          register={register}
          label={getLabel("lotNotes")}
          placeholder={getLabel("lotNotesPlaceholder")}
          users={users}
          user_id={user_id}
          // tooltip={getLabel("lotNotesTooltip")}
          configs={configs}
          operation={operation}
          storeNote={() =>
            storeNote(
              axiosPrivate,
              dispatch,
              setValue,
              getValues,
              getLabel,
              operation,
              user_id,
              userSockets,
              users,
            )
          }
        />
      </div>
      <div className="operation_info_container_row additional">
        <Label label={getLabel("demandant")}>
          {users?.find?.(
            (user) => Number(user.id) === Number(operation?.user_id)
          )?.username || ""}
        </Label>
        <Label label={getLabel("updateDate")}>
          {format(new Date(operation?.update_date || null), configs.dateFormat)}
        </Label>
      </div> */}
    </>
  );
};

export const CutDetail = ({
  operation,
  completed,
  drops,
  users,
  tanks,
  lots,
  configs,
  getValues,
  register,
  errors,
  type,
  dispatch,
  setValue,
  user_id,
  axiosPrivate,
  userSockets,
  isMobile,
  virtualTanks = null,
  isVirtualMode = false,
  isNextToExecute = false,
  protocolApplicationBanner = null,
}) => {
  const [getLabel] = useLabels();
  const { auth } = useAuth();
  const role = auth?.role || ROLES.GUEST;
  const [completedState, setCompletedState] = useState([]);

  const setCheckboxState = (tank_id) => {
    const newCompletedState = [...completedState];
    const index = newCompletedState.indexOf(tank_id);
    if (index === -1) {
      newCompletedState.push(tank_id);
    } else {
      newCompletedState.splice(index, 1);
    }
    setCompletedState(newCompletedState);
  };

  const onNewScanResult = (decodedText, decodedResult) => {
    // handle decoded results here
    console.log("scanned", decodedText, decodedResult);
  };

  const renderFilteringData = () => {
    if (!operation?.start_date) return;

    const { hours, minutes, formatted, className } = getElapsedTimeWithClass(
      new Date(operation?.start_date),
      operation?.completion_date
        ? new Date(operation?.completion_date)
        : new Date(),
      true,
      true
    );

    const isFilteringInProgress = !operation?.completion_date;
    // Remove '+' prefix for filtering (no work_time limit exists)
    const formattedTime = formatted.replace(/^\+/, "");
    const timeElapsedBadge = (
      <div className={`elapsed_time_badge elapsed_time_badge--filtering ${operation?.completion_date ? className : ""}`}>
        <Icon
          icon={isFilteringInProgress ? "fa-spinner" : "fa-hourglass-half"}
          size="sm"
          {...(isFilteringInProgress && { spin: true, style: { color: "#3498db" } })}
        />
        <span className="elapsed_time_badge__value--compact">{formattedTime}</span>
      </div>
    );

    if (operation?.completion_date) {
      const tankData = operation?.dest_tanks?.[0] || {};
      const liters =
        (Number(tankData?.quantity) || 0) + Number(tankData?.drop) || 0;
      const { l_per_min, l_per_hour } = computeFlowRates(
        liters,
        hours,
        minutes
      );

      return (
        <>
          <Label label={getLabel("flowRate")}>
            {l_per_min ? l_per_min : ""} {l_per_min ? "L/min" : ""}
          </Label>
          {timeElapsedBadge}
        </>
      );
    } else
      return (
        <>
          {timeElapsedBadge}
        </>
      );
  };

  const renderFilters = () => {
    const filters = operation?.treatments || [];
    const filterType = filters?.[0]?.type;

    if (filterType && Array.isArray(filters) && filters?.length > 0) {
      return (
        <div>
          <div className="operation_info_container_row">
            <Label label={getLabel("filterType")}>
              {filterType ? getLabel(`filterType_${filterType}`) : ""}
            </Label>
            {operation?.start_date && (
              <div className="stabilization_time">
                <Label label={getLabel("operationStartDate")}>
                  {format(
                    new Date(operation?.start_date || null),
                    configs.dateFormat
                  )}
                </Label>
                {operation?.completion_date && (
                  <Label label={getLabel("stabilizationEndDate")}>
                    {format(
                      new Date(operation?.completion_date || null),
                      configs.dateFormat
                    )}
                  </Label>
                )}
                <Label label={getLabel("operationPriority")}>
                  {getLabel(`operationPriority${operation?.priority}`)}
                </Label>
                {operation?.start_date && renderFilteringData()}
              </div>
            )}
          </div>
          <div className="operation_info_container_row filter_wrapper is_link">
            <div className="filter_in">IN</div>
            {filters?.map((filter, index) => {
              return (
                <div key={`filter-${index}`}>
                  <FilterIcon filter={filter} />
                </div>
              );
            })}
            <div className="filter_out">OUT</div>
          </div>
        </div>
      );
    }
    return;
  };

  const sortByBatchTypeAndTankName = (a, b) => {
    if (a?.batch_type < b?.batch_type) return 1;
    if (a?.batch_type > b?.batch_type) return -1;

    // If batch_type is the same, sort by name
    if (a?.name < b?.name) return -1;
    if (a?.name > b?.name) return 1;

    return 0;
  };

  return (
    <>
      {protocolApplicationBanner}
      {/* <h3>{getLabel(`operationType${operation?.type}`)}</h3> */}
      <div
        className={`operation_info_container_row ${
          operation?.cellarman_ids?.length > 1 ? "start" : ""
        }`}
      >
        <Label label={getLabel("insertionBy")}>
          <div>
            {users?.find?.(
              (user) => Number(user.id) === Number(operation?.user_id)
            )?.username || ""}
          </div>
        </Label>
        <Label label={getLabel("insertionDate")}>
          {format(
            new Date(operation?.insertion_date || null),
            configs.shortDateFormat
          )}
        </Label>
        {operation?.completion_date === null && (
          <>
            <Label label={getLabel("expireDate")}>
              {format(
                new Date(operation?.expire_date || null),
                configs.shortDateFormat
              )}
            </Label>
            <Label label={getLabel("cellarman")}>
              {
                <div className="cellarman_container">
                  {operation?.cellarman_ids?.map((cellarman, index) => {
                    const cellarmanName =
                      users?.find?.(
                        (user) => Number(user.id) === Number(cellarman)
                      )?.username || "";
                    return (
                      <div key={`cellarman-${index}`}>
                        {cellarmanName +
                          (operation?.cellarman_ids?.length > 1 ? " " : "")}
                      </div>
                    );
                  })}
                </div>
              }
            </Label>
          </>
        )}
        {operation?.completion_date && (
          <>
            <Label label={getLabel("operationCompletionUser")}>
              <div>
                {users?.find?.(
                  (user) => Number(user.id) === Number(operation?.completed_by)
                )?.username || ""}
              </div>
            </Label>
            {type !== "FILTERING" && (
              <Label label={getLabel("operationCompletionDate")}>
                {format(
                  new Date(operation?.completion_date || null),
                  configs.dateFormat
                )}
              </Label>
            )}
          </>
        )}
        {/* <Label label={getLabel("cellarman")}>
          {
            <div className="cellarman_container">
              {operation?.cellarman_ids?.map((cellarman) => {
                const cellarmanName =
                  users?.find?.((user) => Number(user.id) === Number(cellarman))
                    ?.username || "";
                return (
                  <div>
                    {cellarmanName +
                      (operation?.cellarman_ids?.length > 1 ? " " : "")}
                  </div>
                );
              })}
            </div>
          }
        </Label> */}
      </div>
      <div className={"confirmation-modal-container"}>
        <h4>{getLabel("operationDetail")}</h4>
        <OperationRecap
          type={type}
          isScale={false}
          src_tanks={getRecapTanks(tanks, operation, true)}
          dest_tanks={getRecapTanks(tanks, operation, false)}
          additionalData={type === "FILTERING" ? operation?.treatments : []}
          // src_tanks={operation?.src_tanks}
          // dest_tanks={operation?.dest_tanks?.map(d => { return { ...d, add_quantity: type === "CUT" ? d?.quantity : d?.arrow_quantity }})}
        />
      </div>
      {/* <h3>{getLabel("fromTanks")}</h3> */}
      <h4>{type === "CUT" ? getLabel("fromTanks") : getLabel("fromTank")}</h4>
      {[...(operation?.src_tanks || [])]
        ?.sort(sortByBatchTypeAndTankName)
        ?.filter((s, index) => (type === "CUT" ? true : index === 0))
        ?.map((srcTank, index) => {
          // trick to avoid multiple src tanks
          return (
            <TankOperationDetail
              isSrc={true}
              tank={srcTank}
              operation={operation}
              register={register}
              errors={errors}
              idx={index}
              tanks={tanks}
              users={users}
              lots={lots}
              type={type}
              role={role}
              completed={completed}
              drops={drops}
              completedState={completedState}
              setCheckboxState={setCheckboxState}
              setValue={setValue}
              getLabel={getLabel}
              isMobile={isMobile}
              virtualTanks={virtualTanks}
              isVirtualMode={isVirtualMode}
              isNextToExecute={isNextToExecute}
            />
          );
        })}
      <h4>
        {type === "CLOSING"
          ? getLabel("toCloseDest")
          : type === "CUT" ||
            type === "FILTERING" ||
            operation?.dest_tanks?.length === 1
          ? getLabel("toTank")
          : getLabel("toTanks")}
      </h4>
      {[...(operation?.dest_tanks || [])]
        ?.sort(sortByBatchTypeAndTankName)
        ?.map((destTank, index) => {
          return (
            <TankOperationDetail
              type={type}
              completed={completed}
              drops={drops}
              role={role}
              users={users}
              tanks={tanks}
              lots={lots}
              tank={destTank}
              operation={operation}
              idx={index}
              register={register}
              errors={errors}
              setValue={setValue}
              getLabel={getLabel}
              isMobile={isMobile}
              virtualTanks={virtualTanks}
              isVirtualMode={isVirtualMode}
              isNextToExecute={isNextToExecute}
            />
          );
        })}
      {/* <div className="operation_info_container_row">
        <Label label={getLabel("operationNotes")} width={400} height={150} customClassName={"notes"}>
          {operation?.note}
        </Label>
      </div> */}
      {operation?.type === "RACKING" && operation?.pomaces?.length > 0 && (
        <>
          <h4>{getLabel("operationPomaces")}</h4>
          <div className="operation_info_container_row">
            {renderPomaces(operation, getLabel)}
          </div>
        </>
      )}
      {operation?.type === "FILTERING" && operation?.treatments?.length > 0 && (
        <>
          <h4>{getLabel("operationFiltering")}</h4>
          <div className="operation_info_container_row">{renderFilters()}</div>
        </>
      )}

      {/* <div className="operation_info_container_row">
        <ChatArea
          name="chat"
          register={register}
          label={getLabel("lotNotes")}
          placeholder={getLabel("lotNotesPlaceholder")}
          users={users}
          user_id={user_id}
          // tooltip={getLabel("lotNotesTooltip")}
          configs={configs}
          operation={operation}
          storeNote={() =>
            storeNote(
              axiosPrivate,
              dispatch,
              setValue,
              getValues,
              getLabel,
              operation,
              user_id,
              userSockets,
              users,
            )
          }
        />
      </div>
      <div className="operation_info_container_row additional">
        <Label label={getLabel("demandant")}>
          {users?.find?.(
            (user) => Number(user.id) === Number(operation?.user_id)
          )?.username || ""}
        </Label>
        <Label label={getLabel("updateDate")}>
          {format(new Date(operation?.update_date || null), configs.dateFormat)}
        </Label>
      </div> */}
    </>
  );
};

export const AnalysisDetail = ({
  operation,
  users,
  tanks,
  lots,
  analysis,
  results,
  configs,
  isCellarmanEnabled,
  registerComplete,
  errorsComplete,
  controlComplete,
  analysisState,
  getValues,
  dispatch,
  user_id,
  setValue,
  register,
  axiosPrivate,
  isMobile,
  previousTreatments,
  data: dataFromParent,
  virtualBatches = null,
  isVirtualMode = false,
  isNextToExecute = false,
  protocolApplication = null,
  protocolApplicationBanner = null,
  // key,
}) => {
  const [getLabel] = useLabels();
  const [data, setData] = useState([]);
  const [minEditableIndex, setMinEditableIndex] = useState(0);
  const [maxEditableIndex, setMaxEditableIndex] = useState(0);

  const displayModeGrid = useSelector(selectAnalysisDisplayGrid);

  const isBatchAnalysis = !operation?.on_tank;
  const isCompleted =
    (!operation?.repetitive &&
      operation?.completion_date !== undefined &&
      operation?.completion_date !== null) ||
    (operation?.repetitive &&
      Number(operation?.completed) === Number(operation?.total));

  // useEffect(() => {
  //   const tmpData = [];

  //   if (!isCompleted && operation?.repetitive) {
  //     if (Number(operation?.completed) > 0) {
  //       setCompletedAnalysisData(tmpData);
  //     }
  //     if (Number(operation?.completed) < Number(operation?.total))
  //       setUncompletedAnalysisData(tmpData);
  //   } else if (isCompleted) setCompletedAnalysisData(tmpData);
  //   else setUncompletedAnalysisData(tmpData);

  //   // setData(tmpData);
  // }, [results, analysis]);

  const getSortedAnalysisData = (analysisArray) => {
    return (
      analysisArray?.sort((a, b) => {
        const aHasTank = a?.tank_id != null && a?.tank_id !== "";
        const bHasTank = b?.tank_id != null && b?.tank_id !== "";

        const aKey = aHasTank ? a.tank_id : a.batch_id || "";
        const bKey = bHasTank ? b.tank_id : b.batch_id || "";

        // Normalizzo i valori null o vuoti per evitare NaN
        const normAKey = typeof aKey === "number" ? aKey : 0;
        const normBKey = typeof bKey === "number" ? bKey : 0;

        if (normAKey !== normBKey) {
          return normAKey - normBKey;
        }

        // se index uguale, confronto translated
        const aTr = getLabel(`substance_${a?.substance_name}`) || "";
        const bTr = getLabel(`substance_${b?.substance_name}`) || "";
        return aTr.localeCompare(bTr);
      }) || []
    );
  };

  useEffect(() => {
    // Usa il data da OperationsDetail e aggiungi i campi calcolati da AnalysisDetail
    if (dataFromParent?.length > 0) {
      const enrichedData = dataFromParent.map((row) => {
        // Estrai il resultValue dai results array
        const resultValue = row?.results?.find(r => r?.substance_id === row?.substance_id)?.value;

        return {
          ...row,
          analyst: row?.analyst || (results?.find(r => r?.operation_id === operation?.id)?.analyst),
          total: row?.total || operation?.total,
          ranges: row?.ranges, // già presente da OperationsDetail
          value: resultValue || row?.value,
        };
      });
      
      setData(enrichedData);
    }

    // Calcola l'intervallo di righe editabili in base al ciclo corrente
    const isOperationCompleted = operation?.completion_date !== null;
    if (!operation?.repetitive || !isOperationCompleted) {
      // Per operazioni non ripetitive o incomplete, tutte le righe sono editabili
      setMinEditableIndex(0);
      setMaxEditableIndex(dataFromParent?.length || 0);
    } else {
      // Per operazioni ripetitive completate, solo il ciclo corrente è editabile
      // Calcola i dati univoci per sostanza e tank
      const uniqueSubstances = new Set(dataFromParent?.map(d => d?.substance_name));
      const uniqueTanks = new Set(dataFromParent?.map(d => d?.tank_id || d?.batch_id));
      const rowsPerCycle = uniqueSubstances.size * uniqueTanks.size;
      const currentCycleIndex = Number(operation?.relative_id) - 1;
      setMinEditableIndex(currentCycleIndex * rowsPerCycle);
      setMaxEditableIndex((currentCycleIndex + 1) * rowsPerCycle);
    }
  }, [dataFromParent, operation, results]);

  const getFilteredSubstances = (substances = [], importDataState = null) => {
    const tmp = [];

    // Grouping function
    const grouped = substances
      ?.filter((s) =>
        importDataState !== null ? s?.states?.includes(importDataState) : true
      )
      ?.reduce((acc, item) => {
        const key = item.substance_name || item.name;
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(item);

        return acc;
      }, {});

    // Convert the result to an array of grouped objects (optional)
    Object.keys(grouped)?.forEach((key) => {
      const items = grouped[key];
      const firstItem = items?.[0];
      const substance_name = getLabel(`substance_${key}`);
      // console.log('grouped key', grouped[key])

      tmp.push({
        name: key,
        substance_name: key,
        substance_name_lower: substance_name?.toLowerCase(),
        unit: firstItem?.unit,
        unit_id: firstItem?.unit_id,
        // unit: firstItem?.unit,
        id: firstItem?.id,
        value: firstItem?.id,
        label: substance_name,
        batch_name: items?.batch_name,
        src_name: items?.src_name,
        items: items,
        states: firstItem?.states,
        opType: "ANALYSIS",
      });
    });

    return tmp?.sort((a, b) =>
      a?.substance_name_lower > b?.substance_name_lower
        ? 1
        : b?.substance_name_lower > a?.substance_name_lower
        ? -1
        : 0
    );
  };

  // const setCompletedAnalysisData = (tmpData) => {
  //   if (!results || results?.length === 0 || !results?.[0]?.results) return;
  //   let isShowFirstLineData = true;
  //   const keys = analysis?.analysis?.map((an) => an?.substance_name) || []; // Object.keys(result?.results);
  //   const len = keys.length || 0;

  //   const anLen = countUniqueAnalysis();
  //   const tanksLen = operation?.src_tanks?.length;
  //   const rowsNum = anLen * tanksLen;

  //   results?.forEach((result, i) => {
  //     const res = result?.results;

  //     // if (!result?.results) return true;
  //     for (let index = 0; index < anLen; index++) {
  //       const currentAnalysis = analysis?.analysis?.find(
  //         (a) => a.substance_id === res?.[index]?.substance_id
  //       );

  //       const name = currentAnalysis?.name || "";
  //       const batch_name = operation?.src_tanks?.find(o => (o?.tank_id || o?.batch_id) === res?.[index]?.tank_id)?.name || "";
  //       const value = res?.[index]?.value || "";
  //       // const currentAnalysis = analysis?.analysis
  //       const completedRowsNum = Number(operation?.completed) * rowsNum;
  //       const totalIndex = i * (len || 0) + index;
  //       tmpData.push({
  //         ...result,
  //         batch_name: batch_name,
  //         results: value,
  //         name: name,
  //         isShowFirstLineData:
  //           totalIndex %
  //             (rowsNum) ===
  //           0,
  //         enableRange: i === 0 || totalIndex >= completedRowsNum && totalIndex < (anLen + completedRowsNum),
  //         min_range: currentAnalysis?.min_range,
  //         max_range: currentAnalysis?.max_range,
  //         src_name:
  //           (operation?.on_tank
  //             ? tanks?.find((t) => t.id == result?.tank_id)?.name
  //             : lots?.find((l) => l.id == result?.batch_id)?.name) || "",
  //         repetitive: operation?.repetitive,
  //         unit: currentAnalysis?.unit || "",
  //         endOfRepBorder: operation?.repetitive
  //           ? !isShowFirstLineData &&
  //             totalIndex %
  //               rowsNum === 0
  //           : false,
  //         // i % operation?.src_tanks?.length === 0 && !isShowFirstLineData,
  //         // endOfSrcNameBorder: !isShowFirstLineData && totalIndex % operation?.src_tanks?.length === 0,
  //         endOfSrcNameBorder:
  //           !isShowFirstLineData &&
  //           totalIndex % anLen === 0,
  //         isTankFirst: index % anLen === 0,
  //         opType: "ANALYSIS",
  //       });
  //       isShowFirstLineData = false;
  //     }

  //     // for (let index = 0; index < len; index++) {
  //     //   const currentAnalysis = analysis?.analysis?.find(
  //     //     (a) => a.substance_name === keys[index]
  //     //   );
  //     //   const completedRowsNum = Number(operation?.completed) * rowsNum;
  //     //   const totalIndex = i * (len || 0) + index;
  //     //   tmpData.push({
  //     //     ...result,
  //     //     results: result?.results?.[keys[index]] || "",
  //     //     name: keys[index],
  //     //     isShowFirstLineData:
  //     //       totalIndex %
  //     //         (rowsNum) ===
  //     //       0,
  //     //     enableRange: i === 0 || totalIndex >= completedRowsNum && totalIndex < (anLen + completedRowsNum),
  //     //     min_range: currentAnalysis?.min_range,
  //     //     max_range: currentAnalysis?.max_range,
  //     //     src_name:
  //     //       (operation?.on_tank
  //     //         ? tanks?.find((t) => t.id == result?.tank_id)?.name
  //     //         : lots?.find((l) => l.id == result?.batch_id)?.name) || "",
  //     //     repetitive: operation?.repetitive,
  //     //     unit: currentAnalysis?.unit || "",
  //     //     endOfRepBorder: operation?.repetitive
  //     //       ? !isShowFirstLineData &&
  //     //         totalIndex %
  //     //           rowsNum === 0
  //     //       : false,
  //     //     // i % operation?.src_tanks?.length === 0 && !isShowFirstLineData,
  //     //     // endOfSrcNameBorder: !isShowFirstLineData && totalIndex % operation?.src_tanks?.length === 0,
  //     //     endOfSrcNameBorder:
  //     //       !isShowFirstLineData &&
  //     //       totalIndex % anLen === 0,
  //     //     isTankFirst: index % anLen === 0,
  //     //     opType: "ANALYSIS",
  //     //   });
  //     //   isShowFirstLineData = false;
  //     // }
  //   });
  // };

  // const populateBaseData = (tmp, result, an) => {
  //   const source = operation?.on_tank
  //     ? tanks?.find((t) => t.id == result?.tank_id)
  //     : lots?.find((l) => l.id == result?.batch_id);

  //   const color = source?.color ? `(${getLabel(`lotColor_${source?.color}`)})` : "";

  //   const ranges = {
  //     min_range: an?.min_range,
  //     max_range: an?.max_range
  //   };

  //   tmp.push({
  //     name: an?.substance_name,
  //     color: color,
  //     substance_id: `${an?.substance_id}`,
  //     unit: an?.unit,
  //     enableRange: Number(operation?.completed) === 0,
  //     ...ranges,
  //     src_name: source?.name || "",
  //     batch_name: source?.batch_name || "",
  //     src_id: source?.id || "",
  //     opType: "ANALYSIS",
  //   });
  // };

  // const countUniqueAnalysis = () => {
  //   return [...new Set(analysis?.analysis?.filter(a => !a?.removed || a?.removed === false)?.map(item => item?.substance_name))]?.length;
  // };

  // const setUncompletedAnalysisData = (tmpData) => {
  //   const tmp = [];
  //   if (results?.filter((an) => an?.result)?.length === 0 || tmpData?.length > 0) {
  //     operation?.src_tanks?.forEach((s) => {
  //     // for (const s of (operation?.src_tanks || [])) {
  //       const result = operation?.on_tank
  //         ? { tank_id: s.tank_id }
  //         : { batch_id: s.batch_id };
  //       const customAnalysisIds = operation?.treatments
  //         ?.filter((t) => t?.valid_tanks?.includes(Number(s?.tank_id) || s?.batch_id) && (!t?.removed || t?.removed === false))
  //         ?.map(s => s?.substance_id) || [];
  //       const customAnalysisPerTank = analysis?.analysis?.filter((a) => customAnalysisIds?.includes(a?.substance_id)) || [];

  //       customAnalysisPerTank?.forEach((an) => {
  //         populateBaseData(tmp, result, an);
  //       });
  //     }
  //     );
  //   }

  //   const anLen = countUniqueAnalysis();
  //   const tanksLen = operation?.src_tanks?.length;
  //   const rowCount =
  //     anLen * operation?.src_tanks?.length || 1;
  //   const analysisCount = anLen;
  //   const sampledCount =  tmpData?.length > 0 ?
  //     Number(operation?.total) - Number(tmpData?.length / rowCount) || 0 : 0;
  //   const missingReplica = operation?.repetitive
  //     ? Number(operation?.total) -
  //       Number(operation?.completed) -
  //       sampledCount -
  //       1
  //     : 0;
  //   // console.log('rowCount', rowCount, 'analysisCount', analysisCount, 'sampledCount', sampledCount)

  //   if (missingReplica > 0) {
  //     const copy = [...tmp];
  //     for (let index = 0; index < missingReplica; index++) {
  //       tmp.push(...copy);
  //     }
  //   }
  //   const basicData = {
  //     total: operation?.total,
  //     repetitive: operation?.repetitive,
  //     // enableRange: false,
  //   };

  //   tmp?.forEach((t, i) => {
  //     const obj = { ...t, ...basicData };

  //     obj.isTankFirst = i % analysisCount === 0;
  //     // obj.isTankFirst =
  //     // i % rowCount === 0 ||
  //     // (operation?.src_tanks?.length > 1 &&
  //     //   i % operation?.src_tanks?.length === 0);
  //     obj.isShowFirstLineData = obj.isTankFirst;    // TO DO, if ok remove isShowFirstLineData and use isTankFirst i % rowCount === 0;
  //     obj.relative_id = Math.floor(tmpData?.length / (tanksLen * anLen)) + 1;
  //     obj.endOfRepBorder = i % rowCount === 0;
  //     obj.endOfSrcNameBorder = obj.isTankFirst;
  //     // obj.endOfSrcNameBorder =
  //     //   operation?.src_tanks?.length === 1
  //     //     ? false
  //     //     : i % operation?.src_tanks?.length === 0 && i > 0;
  //     // obj.enableRange = true;
  //     // console.log('i', i, anLen * ta)
  //     obj.enableRange =
  //       i < (tanksLen * anLen) && Number(operation?.completed) === 0;
  //     obj.isEditable =
  //       Number(operation?.relative_id) === Number(obj?.relative_id);
  //     obj.opType = "ANALYSIS";

  //     const isOnlySampled = results?.filter((r) => r?.results === null)?.length > 0;
  //     if (obj.isShowFirstLineData && obj.isTankFirst && isOnlySampled) { //i === Number(operation?.completed) && isOnlySampled) {
  //       // only sampled
  //       const sample = results?.find((r) => r?.tank_id === t?.src_id);
  //       obj.collection_date = new Date(sample?.collection_date);
  //       obj.temperature = sample?.temperature;
  //       obj.user_id = sample?.user_id;
  //     }

  //     if (
  //       Number(obj.relative_id) <= Number(operation.total) ||
  //       !operation?.repetitive
  //     )
  //       tmpData.push(obj);
  //   });
  // };

  const columnHelper = createColumnHelper();
  const columns = [
    // columnHelper.accessor((row) => `${row?.isTankFirst ? row.src_name : ""}`, {
    columnHelper.accessor("src_name", {
      header: operation?.on_tank ? getLabel("tank") : getLabel("lot"),
      cell: TableCell,
      meta: {
        filterVariant: "text",
        customClassName: "hide",
      },
    }),
  ];
  
  // no src_batch in case of ANALYSIS on grape batch
  if (
    operation?.on_tank ||
    (!operation?.on_tank && operation?.src_tanks?.[0]?.tank_id !== null)
  )
    columns.push(
      columnHelper.accessor("batch_name", {
        header: getLabel("lot"),
        cell: TableCell,
        meta: {
          isFilterDisabled: true, // TO DO: fix search not working
          customClassName: "hide",
        },
      })
    );

  if (operation?.repetitive) {
    columns.unshift(
      columnHelper.accessor(
        (row) =>
          `${row?.isShowFirstLineData ? row.relative_id : ""}${
            row?.isShowFirstLineData ? "/" + row?.total : ""
          }`,
        {
          header: getLabel("analysisRepetition"),
          cell: TableCell,
          meta: {
            isFilterDisabled: true,
          },
        }
      )
    );
  }
  
  // if (!isCompleted) {
  // columns.push(

  // columnHelper.accessor("quantity", {
  //   header: getLabel("examResult"),
  //   cell: TableCell,
  //   meta: {
  //     type: "number",
  //     editable: true,
  //     regExpPattern: INT_FLOAT_REGEX,
  //     register: registerComplete,
  //   },
  // })
  // );
  // }
  // if (isCellarmanEnabled && Number(operation?.completed) < Number(operation?.total)) {
  
  // console.log('analysisState', analysisState)
  
  if (analysisState !== "NOT_SAMPLED") {
    columns.push(
      columnHelper.accessor(
        (row) =>
          !row?.isShowFirstLineData
            ? ""
            : `${
                row?.collection_date
                  ? format(
                      new Date(row.collection_date || null),
                      configs.hourMediumDateFormat
                    )
                  : ""
              } (${
                users?.find((u) => u.id === Number(row.user_id))?.username ||
                "-"
              }) - ${row?.temperature ? row.temperature + " \u00b0C" : ""}`,
        {
          header: getLabel("analysisCollection"),
          cell: TableCell,
          meta: {
            isFilterDisabled: true,
          },
        }
      )
    );
  }

  //   if (analysisState !== 'NOT_SAMPLED') {
  //       columns.push(
  //         columnHelper.accessor(
  //           (row) =>
  //             !row?.isShowFirstLineData
  //               ? ""
  //               : users?.find((u) => u.id === Number(row.user_id))?.username || "—",
  //           {
  //             header: getLabel("analysisCollectionUser"),
  //             cell: TableCell,
  //             meta: {
  //               isFilterDisabled: true,
  //             }
  //           }
  //         ),

  //     columnHelper.accessor(
  //       (row) =>
  //         !row?.isShowFirstLineData
  //           ? ""
  //           : row?.collection_date
  //           ? format(new Date(row.collection_date || null), configs.dateFormat)
  //           : '',
  //       {
  //         header: getLabel("analysisCollectionDate"),
  //         cell: TableCell,
  //         meta: {
  //           isFilterDisabled: true,
  //         },
  //       }
  //     ),
  //     columnHelper.accessor(
  //       (row) =>
  //         `${
  //           row?.isTankFirst && row?.temperature
  //             ? row.temperature + " \u00b0C"
  //             : row?.isShowFirstLineData ? "-" : ""
  //         }`,
  //       {
  //         header: getLabel("analysisExamTemperature"),
  //         cell: TableCell,
  //         meta: {
  //           isFilterDisabled: true,
  //         },
  //       }
  //     )
  //   );
  // }

  columns.push(
    columnHelper.accessor(
      (row) => getLabel(`substance_${row?.substance_name}`),
      {
        // + (row?.unit?.trim()?.length > 0 ? ` (${row.unit})` : ''), {
        header: getLabel("analysisName"),
        cell: TableCell,
        meta: {
          filterVariant: "text",
        },
      }
    )
  );

  if (operation?.type === "ANALYSIS") {
    columns.push(
      // columnHelper.accessor(
      //   (row) =>
      //     row?.enableRange || analysisState === "INSERTING"
      //       ?`${row?.min_range ? String(row?.min_range) : 'n/a'} - ${row?.max_range ? String(row?.max_range) : 'n/a'} ${row?.unit}`
      //       : "",
      //       {
      //     header: getLabel("substanceRange"),
      //     cell: TableCell,
      //     meta: {
      //       isFilterDisabled: true,
      //       additional: 'examColor'
      //     }
      //   }
      // ),
      columnHelper.accessor("ranges", {
        header: getLabel("substanceRange"),
        cell: MultipleRowsCell,
        meta: {
          array: "items",
          selector: "ranges",
          isFilterDisabled: true,
        },
      })
    );
  }

  if (
    (analysisState === "INSERTING" || analysisState === "STORED") &&
    (operation?.sampled ||
      Number(operation?.completed) <= Number(operation?.total))
  ) {
    columns.push(
      columnHelper.accessor(
        (row) =>
          row?.results ? row.results + (row?.unit ? ` ${row?.unit}` : "") : "",
        {
          header:
            operation.type === "ANALYSIS"
              ? getLabel("examResult")
              : getLabel("drugQuantity"),
          cell: TableCell,
          meta: {
            type: "number",
            editable: true,
            register: registerComplete,
            noStoreValue: true,
            isFilterDisabled: true,
            isCompleted: true,
          },
        }
      )
    );
  }

  // TO DO: quando filtri per analisi o tank, la colonna # 1/4 scompare
  // if (analysisState && analysisState !== "NOT_SAMPLED") { //Number(operation?.completed) > 0) {
  //   columns.push(
  //     columnHelper.accessor(
  //       (row) =>
  //         !row?.isShowFirstLineData
  //           ? ""
  //           : row?.collection_date
  //           ? format(new Date(row.collection_date || null), configs.dateFormat)
  //           : '-',
  //       {
  //         header: getLabel("analysisCollectionDate"),
  //         cell: TableCell,
  //         meta: {
  //           isFilterDisabled: true,
  //         },
  //       }
  //     ));
  // }
  if (Number(operation?.completed) > 0 && analysisState === "STORED") {
    columns.push(
      columnHelper.accessor(
        (row) =>
          !row?.isShowFirstLineData ? "" : row?.analyst ? row.analyst : "-",
        {
          header: getLabel("analysisAnalyst"),
          cell: TableCell,
          meta: {
            isFilterDisabled: true,
          },
        }
      )
    );
  }

  //   if (isCellarmanEnabled && Number(operation?.completed) < Number(operation?.total)) {
  //     columns.push(
  //       columnHelper.display({
  //         id: "edit",
  //         cell: EditCell,
  //         meta: {
  //           isEditable: true,
  //         },
  //       }),
  //     );
  // }

  // if (isCellarmanEnabled) {

  //   // if (!isCompleted && Number(operation?.completed) === 0) {
  //   //   columns.push(
  //   //     columnHelper.accessor((row) => (!row?.enableRange ? "" : row?.unit), {
  //   //       header: getLabel("unit"),
  //   //       cell: TableCell,
  //   //     }),
  //   //   )
  //   // }

  // } else {
  //   columns.push(
  //     columnHelper.accessor(
  //       (row) =>
  //         !row?.isFirst
  //           ? ""
  //           : row?.results
  //           ? row.results + " " + row?.unit
  //           : "-",
  //       {
  //         header: getLabel("substanceResult"),
  //         cell: TableCell,
  //       }
  //     ),

  //   );
  // }

  const renderAnalysisEvolution = () => {
    return (
      <div>
        <div className="primary_container_menu">
          {/* <h4>{getLabel('nextOperations')}</h4> */}
        </div>
        {results?.length > 0 && (
          <AnalysisEvolution
            configs={configs}
            analysisResults={results}
            lots={[
              ...new Set(
                results
                  ?.map((r) =>
                    lots?.find((l) => l.id == r.batch_id && r.tank_id === null)
                  )
                  .filter(Boolean)
              ),
            ]}
            tanks={[
              ...new Set(
                results
                  ?.map((r) => tanks?.find((t) => t.id == r.tank_id))
                  .filter(Boolean)
              ),
            ]}
            exams={[analysis]?.filter((exam) =>
              [operation]?.find(
                (an) => Number(exam.protocols_id) === Number(an.protocol_id)
              )
            )}
          />
        )}
      </div>
    );
  };

  const toggleDisplayMode = () => {
    dispatch(toggleAnalysisDisplayGrid());
  };

  const getLotData = (l) => {
    const lot = lots?.find((ll) => l?.batch_id === ll?.id) || null;
    //  TO DO> se faccio analisi su lotto pieno, poi programmo DESTEMMING
    if (!lot || operation?.completion_date) {
      // if lot is not found, return src_tanks data (for instance if the GRAPE src_tank is now empty or dectivated due to a DESTEMMING)
      return {
        name: l?.old_batch_name || "",
        color: l?.color,
        state: l?.state,
        initial_quantity: l?.initial_quantity,
        real_final_quantity: l?.old_quantity,
        composition: l?.composition,
      };
    }

    // Protocol application mode: simplify lot display
    if (operation?.protocol_application_id) {
      if (isVirtualMode && virtualBatches?.[l?.batch_id]) {
        // Stato Simulato: use virtual batch
        const vBatch = virtualBatches[l?.batch_id];
        return {
          name: vBatch?.name || lot?.name || lot?.label || "",
          color: vBatch?.color || lot?.color,
          state: vBatch?.state || lot?.state,
          initial_quantity: vBatch?.quantity,
          quantity: vBatch?.quantity || 0,
          real_initial_quantity: vBatch?.quantity || 0,
          real_final_quantity: vBatch?.quantity || 0,
          is_planned: false,
          composition: vBatch?.composition || lot?.composition,
          on_tank: operation?.on_tank,
        };
      } else {
        // Stato Reale: ignore is_planned/planned_pomaces logic
        return {
          name: lot?.name || lot?.label || "",
          color: lot?.color,
          state: lot?.state,
          initial_quantity: lot?.quantity,
          quantity: lot?.quantity || 0,
          real_initial_quantity: lot?.quantity || 0,
          real_final_quantity: lot?.quantity || 0,
          is_planned: false,
          composition: lot?.composition,
          on_tank: operation?.on_tank,
        };
      }
    }

    const total_pomaces = Number(lot?.planned_pomaces
        ?.reduce((acc, curr) => {
        return acc + (Number(curr?.quantity) || 0);
      }, 0)) || 0;

    // Build the lotData object
    const lotData = {};
    lotData.name = lot?.name || lot?.label || "";
    lotData.color = lot?.color;
    lotData.initial_quantity = lot?.quantity; // Fixed initial capacity (e.g., 100 Kg)
    lotData.quantity = Number(lot?.quantity) || 0;
    lotData.planned_quantity = Number(lot?.planned_quantity) || 0;
    lotData.real_initial_quantity = Number(lot?.quantity) || 0;
    lotData.estimated_initial_quantity = lot?.is_planned
      ? (Number(lot?.actual_quantity) || 0) -
        (Number(lot?.planned_quantity) || 0)
      : Number(lot?.actual_quantity) || 0;
    lotData.real_final_quantity = (lot?.is_planned
      ? Number(lot?.planned_quantity) || 0
      : Number(lot?.actual_quantity)) - total_pomaces;
    lotData.estimated_final_quantity = Number(lot?.planned_quantity) || 0;
    lotData.is_planned = lot?.is_planned === true; //(Number(lot?.planned_quantity) || 0) < Number(lot?.quantity);
    lotData.composition = lot?.composition;
    lotData.on_tank = operation?.on_tank;

    return lotData;
  };

  return (
    <div key={`analysis-detail-${operation?.id}`}>
      {/* <h4>{getLabel("analysisProtocols")}</h4> */}
      <h4>{getLabel("operationDetail")}</h4>
      <div className="operation_config_with_banner">
        <div className="config_section">
          <div className="operation_info_container_row">
            <Label label={getLabel("insertionBy")}>
              <div>
                {users?.find?.(
                  (user) => Number(user.id) === Number(operation?.user_id)
                )?.username || ""}
              </div>
            </Label>
            <Label
              label={
                operation?.completion_date === null
                  ? getLabel("insertionDate")
                  : getLabel("operationCompletionDate")
              }
            >
              {format(
                new Date(operation?.insertion_date || null),
                operation?.completion_date === null
                  ? configs.shortDateFormat
                  : configs.dateFormat
              )}
            </Label>
            <Label label={getLabel("expireDate")}>
              {format(
                new Date(operation?.expire_date || null),
                configs.shortDateFormat
              )}
            </Label>
            <Label label={getLabel("cellarman")}>
              {
                <div className="cellarman_container">
                  {operation?.cellarman_ids?.map((cellarman, index) => {
                    const cellarmanName =
                      users?.find?.((user) => Number(user.id) === Number(cellarman))
                        ?.username || "";
                    return (
                      <div key={`cellarman-${index}`}>
                        {cellarmanName +
                          (operation?.cellarman_ids?.length > 1 ? " " : "")}
                      </div>
                    );
                  })}
                </div>
              }
            </Label>
            <Label label={getLabel("operationPriority")}>
              {getLabel(`operationPriority${operation?.priority}`)}
            </Label>
          </div>
        </div>
        <div className="banner_section">
          {protocolApplicationBanner}
        </div>
      </div>
      <div className="analysis_table">
        <h4>{getLabel("analysisProtocols")}</h4>
        <Label label={getLabel("protocolName")}>
          <div className="analysis_detail_header">
            {analysis?.protocols_name}
            {operation?.metadata?.source === 'field_analysis' && (
              <span className="analysis_detail_field_analysis_badge">
                {getLabel("lotFieldAnalysis")}
              </span>
            )}
          </div>
        </Label>
        {/* </div> */}
        {/* <h4 className="analysis_title">{analysis?.protocols_name}</h4> */}
        <div className="analysis_switch">
          <Switch
            leftOption={{
              label: getLabel("listView"),
            }}
            rightOption={{
              label: getLabel("gridView"),
            }}
            toggleHandler={toggleDisplayMode}
            status={displayModeGrid}
          />
          <div>
            {data?.length > 0 && (
              <TableEdit
                defaultData={data}
                columns={columns}
                errors={errorsComplete}
                control={controlComplete}
                isEditable={true}
                isSorting={false}
                isMobile={displayModeGrid || isMobile}
                isSearchBar={false}
                analysisState={analysisState}
                filterType={"analysis"}
                minEditableIndex={minEditableIndex}
                maxEditableIndex={maxEditableIndex}
                isLegend={false}
                newRow={{
                  quantity: "",
                }}
                setValue={setValue}
                getValues={getValues}
                type={"analysisDetail"}
                // key={key}
              />
            )}
          </div>
        </div>
      </div>
      {!isBatchAnalysis && (
        <>
          <h4>{getLabel("fromTanksAnalysis")}</h4>
          <div className="operation_info_container analysis_tanks">
            {operation?.src_tanks?.map((srcTank, index) => {
              return (
                <div
                  className="operation_info_container_row"
                  key={`dest-${index}`}
                >
                  <DestTankDetail
                    key={`src-an-${srcTank.id}`}
                    // tank={getTankData(srcTank)}
                    tank={srcTank}
                    isCurrentStateOnly={true}
                    isDrop={false}
                  ></DestTankDetail>
                  {/* <TankIcon
                  tank={operation?.completion_date !== null ? {  // TO DO: add ...(tanks.find())
                    name: tanks.find(
                      (tank) => Number(tank.id) === Number(srcTanks?.tank_id))?.name,
                    capacity: tanks.find(
                      (tank) => Number(tank.id) === Number(srcTanks?.tank_id))?.capacity,
                    quantity: tanks.find(
                      (tank) => Number(tank.id) === Number(srcTanks?.tank_id))?.quantity,
                    color: tanks.find(
                      (tank) => Number(tank.id) === Number(srcTanks?.tank_id))?.color
                  } : tanks.find(
                    (tank) => Number(tank.id) === Number(srcTanks?.tank_id)
                  )}
                  isText={false}
                  isReverse={true}
                  isRecap={false}
                  isName={true}
                  isBatchName={true}
                /> */}
                </div>
              );
            })}
          </div>
        </>
      )}
      {isBatchAnalysis && (
        <>
          <h4>{getLabel("fromLotsAnalysis")}</h4>
          <div className="operation_info_container_row">
            {operation?.src_tanks?.map((srcTank, index) => {
              return (
                // <BatchDetail
                //   key={`src-${srcTank.id}`}
                //   lot={lots.find(
                //     (lot) => Number(lot.id) === Number(srcTanks?.batch_id)
                //   )}
                // ></BatchDetail>
                <DestLotDetail
                  lot={getLotData(srcTank)}
                  isRecap={true}
                  isDestemming={true}
                  // isMobile={isMobile}
                  isAdditional={true}
                  isName={true}
                />
              );
            })}
          </div>
        </>
      )}
      {isCellarmanEnabled && (
        <div>
          {/* <Input
            name="temperature"
            register={registerComplete}
            type="number"
            label={getLabel('analysisExamTemperature')}
            placeholder={getLabel('analysisExamTemperaturePlaceholder') + " (\u00b0C)"}
            error={errorsComplete['temperature']}
            min={{value: 15, message: getLabel('errorMinValue', {value: 15})}}
            max={{value: 35, message: getLabel('errorMaxValue', {value: 35})}}
            // regExpPattern={{value: ALPHA_SPACE_APEX_REGEX, message: getLabel('errorAlphaSpaceApex')}}
        /> */}
        </div>
      )}
      {/* <div className="operation_info_container_row">
        <ChatArea
          name="chat"
          register={register}
          label={getLabel("lotNotes")}
          placeholder={getLabel("lotNotesPlaceholder")}
          users={users}
          user_id={user_id}
          // tooltip={getLabel("lotNotesTooltip")}
          configs={configs}
          operation={operation}
          storeNote={() =>
              storeNote(
              axiosPrivate,
              dispatch,
              setValue,
              getValues,
              getLabel,
              operation,
              user_id,
              userSockets,
              users,
            )
          }
        />
      </div>
      {operation.id !== undefined && renderAnalysisEvolution()}
      <div className="operation_info_container_row additional">
        <Label label={getLabel("demandant")}>
          {users?.find?.(
            (user) => Number(user.id) === Number(operation?.user_id)
          )?.username || ""}
        </Label>
        <Label label={getLabel("updateDate")}>
          {format(new Date(operation?.update_date || null), configs.dateFormat)}
        </Label>
      </div> */}
    </div>
  );
};

export const AutomaticDetail = ({
  type,
  operation,
  completed,
  users,
  tanks,
  configs,
  lots,
  drugs,
  register,
  errors,
  getValues,
  dispatch,
  user_id,
  setValue,
  axiosPrivate,
  isMobile,
  handleOpenModal,
  watchedDrugBatches,
  virtualTanks = null,
  isVirtualMode = false,
  isNextToExecute = false,
  protocolApplicationBanner = null,
}) => {
  const [getLabel] = useLabels();
  const { auth } = useAuth();
  const role = auth?.role || ROLES.GUEST;
  // const [completedState, setCompletedState] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [isShowCost, setIsShowCost] = useState(false);

  useEffect(() => {
    const tmp = [];
    if (
      Array.isArray(operation?.treatments) &&
      operation?.treatments?.length > 0
    ) {
      tmp.push(...operation?.treatments);
    }
    
    const aggregated = aggregateDrugsById(tmp) || [];
    setTableData(aggregated?.map(a => { return { ...a, opType: "TREATMENTS" }}));
  }, [operation]);

  const columnHelper = createColumnHelper();
  const columns = ["HOMOGENIZATION_MASS", "OXYGENATION"].includes(
    operation?.type
  )
    ? [
        columnHelper.accessor(
          (row) => (row?.type ? getLabel(`protocolOperation_${row.type}`) : ""),
          {
            header: getLabel("protocolOperation"),
            cell: TableCell,
            meta: {
              type: "text",
              isFilterDisabled: true,
            },
          }
        ),
        columnHelper.accessor(
          (row) =>
            row?.protocol_type
              ? ["MICRO", "MACRO", "CUSTOM"].includes(row?.protocol_type)
                ? getLabel(
                    `protocolOXYGENATION_${row?.protocol_type}`
                  )
                : ["HOMOGENIZATION_TEMP", "HOMOGENIZATION_REASSEMBLY"].includes(row?.protocol_type)
                ? getLabel(
                    `protocolType_HOMOGENIZATION_${row?.protocol_type.replace("HOMOGENIZATION_", "")}`
                  ) +
                  (row?.protocol_type === "HOMOGENIZATION_REASSEMBLY" &&
                  row?.homogenization_type
                    ? ` (${getLabel(
                        `protocolHomogenizationMass_${row?.homogenization_type}`
                      )})`
                    : "")
                : ""
              : "",
          // row?.protocol_type === "HOMOGENIZATION_MASS" ? getLabel(`protocolType_HOMOGENIZATION_${row?.protocol_type}`) +
          //   (row?.protocol_type === "HOMOGENIZATION_REASSEMBLY" &&
          //   row?.homogenization_type
          //     ? ` (${getLabel(
          //         `protocolHomogenizationMass_${row?.homogenization_type}`
          //       )})`
          //     : "") +
          //   (row?.protocol_type === "CUSTOM"
          //     ? ` (${row?.oxygenation_dose_hours}h:${row?.oxygenation_dose_minutes}m)`
          //     : "")
          // : "",
          {
            header: getLabel("protocolType"),
            cell: TableCell,
            meta: {
              type: "text",
              isFilterDisabled: true,
            },
          }
        ),
        ...(operation?.treatments?.[0]?.protocol_type === "HOMOGENIZATION_REASSEMBLY"
          ? [
              columnHelper.accessor(
                (row) =>
                  row?.work_time &&
                  (row?.pause_time || row?.homogenization_pause_time)
                    ? `${row?.work_time} min/${
                        row?.pause_time || row?.homogenization_pause_time
                      }h ${
                        (type === "CIP" ||
                          row.operation === "HOMOGENIZATION_MASS") &&
                        row?.iter_per_day
                          ? `x ${row?.iter_per_day}`
                          : ""
                      }`
                    : "",
                {
                  header: getLabel("protocolWorkPauseTime"),
                  cell: TableCell,
                  meta: {
                    type: "number",
                    isFilterDisabled: true,
                  },
                }
              ),
              columnHelper.accessor(
                (row) => (row?.motor_speed ? row?.motor_speed + " %" : ""),
                {
                  header: getLabel("protocolMotorSpeed"),
                  cell: TableCell,
                  meta: {
                    type: "number",
                    isFilterDisabled: true,
                  },
                }
              ),
            ]
          : operation?.type === "OXYGENATION" ||
            operation?.protocol_type === "HOMOGENIZATION_TEMP"
          ? [
              columnHelper.accessor(
                (row) =>
                  row?.quantity
                    ? row?.protocol_type === "HOMOGENIZATION_TEMP"
                      ? `${row?.quantity} \u00b0C`
                      : (row?.quantity || 0) +
                        " " +
                        (row?.type === "OXYGENATION" && row.protocol_type
                          ? row?.protocol_type === "CUSTOM"
                            ? `${getLabel("protocolOxygenationNoDose_CUSTOM")} ${
                                row?.oxygenation_dose_hours || ""
                              }h:${row?.oxygenation_dose_minutes || ""}m`
                            : getLabel(`protocolOxygenationNoDose_${row.protocol_type}`)
                          : "")
                    : "",
                {
                  header: getLabel(
                    operation?.type === "HOMOGENIZATION_MASS"
                      ? "sensorSetPoint"
                      : type !== "CIP"
                      ? "protocolQuantity"
                      : "protocolConcentration"
                  ),
                  cell: TableCell,
                  meta: {
                    type: "number",
                    isFilterDisabled: true,
                  },
                }
              ),
            ]
          : []),
        columnHelper.accessor(
          (row) => row?.iter_per_day ? `x${row?.iter_per_day}` : "—" || "",
          {
            header: getLabel("protocolHomogenizationIterPerDay"),
            cell: TableCell,
            meta: {
              type: "text",
              isFilterDisabled: true,
            },
          }
        ),
      ]
    : [
        // columnHelper.accessor(
        //   (row) => getLabel(`protocol_type_${row?.protocol_type}`),
        //   {
        //     header: getLabel("protocol_type"),
        //     cell: TableCell,
        //     isFilterDisabled: true,
        //   }
        // ),
        columnHelper.accessor("substance_name", {
          header: getLabel("treatmentName"),
          cell: TableCell,
        }),
        columnHelper.accessor((row) => `${row?.quantity} ${row?.unit || ""}`, {
          header: getLabel("drugQuantity"),
          cell: TableCell,
          meta: {
            type: "number",
            isFilterDisabled: true,
          },
        }),
        // columnHelper.accessor('product_unit', {
        //   header: getLabel("unit"),
        //   cell: TableCell,
        // }),
        // columnHelper.accessor((row) => getDrugData(row, "range"), {
        //   header: getLabel("substanceRange"),
        //   cell: TableCell,
        //   meta: {
        //     type: "number",
        //     isFilterDisabled: true,
        //   },
        // }),
        columnHelper.accessor(
          (row) =>
            row?.protocol_type
              ? getLabel(`drugLabelGroup_${row?.protocol_type}_One`)
              : "",
          {
            header: getLabel("drugType"),
            cell: TableCell,
            meta: {
              type: "text",
              isFilterDisabled: true,
              filterVariant: "text",
            },
          }
        ),
        columnHelper.accessor(
          (row) =>
            `${row?.min_temperature ? String(row?.min_temperature) : "n/a"} - ${
              row?.max_temperature ? String(row?.max_temperature) : "n/a"
            } \u00b0C` || "",
          {
            header: getLabel("substanceTemperature"),
            cell: TableCell,
            meta: {
              type: "number",
              isFilterDisabled: true,
            },
          }
        ),
        columnHelper.accessor(row => Array.isArray(row.drug_batches)
          ? [...new Set(row.drug_batches.map(b => b.batch_number))].join(", ")
          : "", {
          header: getLabel("drugBatchNumbers"),
          cell: TableCell,
          meta: {
            isFilterDisabled: true,

          },
        }),
      ];

  if (isShowCost) {
    columns.push(
      columnHelper.accessor(
        (row) =>
          `${row?.drug_batch_cost || ""} ${row?.drug_batch_cost ? "€" : ""}`,
        {
          header: getLabel("drugBatchProductCost"),
          cell: TableCell,
          meta: {
            isFilterDisabled: true,
          },
        }
      )
    );
  }
  
  if (
    operation?.completion_date === null &&
    ((Number(role) === ROLES.USER && operation?.cellarman_ids?.includes(auth?.user_id)) ||
      (Number(role) === ROLES.CLIENT && users?.filter((u) => u?.type === "USER")?.length === 0))) {
      columns.push(
        columnHelper.display({
          id: "edit",
          cell: EditCell,
          meta: {
            isEditable: true,
            type: "drugBatchUpdate"
          },
        })
      );
  }
    
  const renderElapsedTime = () => {
    if (!operation?.start_date) return;

    if (operation?.completion_date) {
      const { formatted, className } = getElapsedTimeWithClass(
        new Date(operation?.start_date),
        new Date(operation?.completion_date),
        true,
        true
      );

      // when resin stabilization, there is no work_time
      const statusClassName = type === "STABILIZATION_RESIN" ? "" : className;
      return (
        <div className={`elapsed_time_badge elapsed_time_badge--detailed ${statusClassName}`}>
          <div className="elapsed_time_badge__icon">
            <Icon icon="fa-clock" />
          </div>
          <div className="elapsed_time_badge__content">
            <span className="elapsed_time_badge__value">{formatted}</span>
            <span className="elapsed_time_badge__label">
              {type === "STABILIZATION_RESIN" ? "Stabilizing" : "Completed"}
            </span>
          </div>
          {statusClassName && (
            <div className={`elapsed_time_badge__status-dot elapsed_time_badge__status-dot--${statusClassName}`} />
          )}
        </div>
      );
    }
    const workTime = Number(operation?.treatments?.[0]?.work_time) || 0;
    const isStabilizationStarted = Boolean(operation?.start_date) === true;
    const elapsedDate = isStabilizationStarted
      ? addHours(
          new Date(operation?.start_date),
          type === "STABILIZATION_RESIN" ? workTime : 24 * workTime
        )
      : null;

    let classNameDate;
    let formattedDate;

    const isResinStabilization = type === "STABILIZATION_RESIN";
    const isDynamicStabilization = type === "STABILIZATION_DYNAMIC";

    const endDate = isDynamicStabilization
      ? computeDynamicStabilizationEndDate(operation)
      : new Date();
    if (isDynamicStabilization) {
      const isExpired = new Date() > endDate;
      const { formatted, className } = getElapsedTimeWithClass(
        isExpired ? endDate : new Date(operation?.start_date),
        new Date(),
        isStabilizationStarted
      );
      classNameDate = isExpired ? className : null;
      formattedDate = formatted;
    } else {
      const { formatted, className } = getElapsedTimeWithClass(
        elapsedDate,
        endDate,
        isStabilizationStarted
      );
      classNameDate = className;
      formattedDate = formatted;
      if (isResinStabilization)
        formattedDate = formattedDate.replace(/^\+/, "");
    }

    // when resin stabilization, there is no work_time
    const statusClassName = type === "STABILIZATION_RESIN" ? "" : classNameDate;
    return (
      <div className={`elapsed_time_badge elapsed_time_badge--detailed ${statusClassName}`}>
        <div className="elapsed_time_badge__icon">
          <Icon icon="fa-spinner" spin style={{ color: "#3498db" }} />
        </div>
        <div className="elapsed_time_badge__content">
          <span className="elapsed_time_badge__value">{formattedDate}</span>
          <span className="elapsed_time_badge__label">
            {getLabel("stabilizationInProgress")}
            {/* {type === "STABILIZATION_RESIN" ? getLabel("stabilizationInProgress") : "In Progress"} */}
          </span>
        </div>
        {statusClassName && (
          <div className={`elapsed_time_badge__status-dot elapsed_time_badge__status-dot--${statusClassName}`} />
        )}
      </div>
    );
  };

  const renderStartedOperationBadge = () => {
    if (!operation?.start_date) return null;

    const timingInfo = getStartedOperationTimingInfo(operation);
    if (!timingInfo.elapsed) return null;

    // Determine label based on operation type and subtype
    let labelKey = "operationStatusStarted"; // default fallback
    const subtype = operation.treatments?.[0]?.protocol_type;

    if (type === "OXYGENATION") {
      labelKey = "oxygenationInProgress";
    } else if (type === "FILTERING") {
      labelKey = "operationStatusStarted";
    } else if (type === "HOMOGENIZATION_MASS") {
      if (subtype === "HOMOGENIZATION_TEMP") {
        labelKey = "tempInProgress";
      } else if (subtype === "HOMOGENIZATION_REASSEMBLY") {
        labelKey = "reassemblyInProgress";
      }
    }

    return (
      <div className={`elapsed_time_badge elapsed_time_badge--detailed ${timingInfo.elapsedClass}`}>
        <div className="elapsed_time_badge__icon">
          <Icon icon="fa-spinner" spin style={{ color: "#3498db" }} />
        </div>
        <div className="elapsed_time_badge__content">
          <span className="elapsed_time_badge__value">
            {timingInfo.elapsed}
            {timingInfo.remaining && ` · ${timingInfo.remaining}`}
          </span>
          <span className="elapsed_time_badge__label">
            {getLabel(labelKey)}
          </span>
        </div>
        {timingInfo.elapsedClass && (
          <div className={`elapsed_time_badge__status-dot elapsed_time_badge__status-dot--${timingInfo.elapsedClass}`} />
        )}
      </div>
    );
  };

  const renderStabilization = () => {
    const treatmentData = operation?.treatments?.[0];
    const drugId = treatmentData?.substance_id;
    const batches = treatmentData?.drug_batches || [];
    const totalTreatmentVolume = operation?.src_tanks?.reduce((acc, curr) => {
      return acc + (Number(curr?.arrow_quantity) || 0);
    }, 0) || 0;
    const isEnoughStock = getIsEnoughStock(drugId, batches, totalTreatmentVolume);

    const showBatches = true || operation?.completion_date || (role === 3 ||
      (role === ROLES.CLIENT &&
        users?.filter((u) => u?.type === "USER")?.length ===
          0));

    // Determine which fields to show based on stabilization type
    const isResin = type === "STABILIZATION_RESIN";
    const isDynamic = type === "STABILIZATION_DYNAMIC";
    const isStatic = type === "STABILIZATION_STATIC";

    return (
      <div>
        <div className="operation_info_container_row align_top">
          {treatmentData?.stabilization_type && (
            <Label label={getLabel("stabilizationType")}>
              {getLabel(`physicalProtocol${treatmentData?.stabilization_type}`) || "—"}
            </Label>
          )}
          {!isResin && (
            <Label label={getLabel("stabilizationTemperature")}>
              {treatmentData?.temperature || "—"} {"\u00b0C"}
            </Label>
          )}
        </div>
        {isDynamic && (
            <div className="operation_info_container_row">
              <Label label={getLabel("protocolStabilizationProduct")}>
                {treatmentData?.substance_name || "—"}
              </Label>
              <Label label={getLabel("stabilizationQuantity")}>
                {treatmentData?.quantity || "—"} {treatmentData?.unit || ""}
              </Label>
              {showBatches && batches?.length > 0 && (
                <Label label={getLabel("treatmentConfiguredDrugBatches")}>
                  <div className="drug_batch_wrapper">
                    {batches.map((field, i) => (
                      <div key={`batch_${i}`}>{field?.batch_number} {batches?.length > 1 ? getUsedQuantity({unit_id: treatmentData?.unit_id}, field) : ""}</div>
                    ))}
                    <p>{(batches?.length === 0 || !isEnoughStock) && <span>-</span>}</p>
                  </div>
                </Label>
              )}
            </div>
          )}
        <div className="operation_info_container_row">
          {(isDynamic || isStatic) && treatmentData?.work_time && (
            <Label label={getLabel(isStatic ? "stabilizationWorkTime" : "protocolWorkTime")}>
              {treatmentData?.work_time || "—"}{" "}
              {treatmentData?.work_time_unit || getLabel(
                Number(treatmentData?.work_time) > 1
                  ? "repeatSize_DAY"
                  : "repeatSize_1DAY"
              )?.toLowerCase?.()}
            </Label>
          )}
          {isDynamic && (
            <>
              <Label label={getLabel("protocolPauseTime")}>
                {treatmentData?.pause_time || "—"}{" "}
                {treatmentData?.pause_time_unit || ""}
              </Label>
              <Label label={getLabel("protocolMotorSpeed")}>
                {treatmentData?.motor_speed || "—"} %
              </Label>
              <Label label={getLabel("protocolHomogenizationIterPerDay")}>
                {treatmentData?.iter_per_day ? `x${treatmentData?.iter_per_day}` : "—"}
              </Label>
            </>
          )}
          {isResin && (
            <Label label={getLabel("stabilizationPh")}>
              {treatmentData?.stabilization_ph || "—"}
            </Label>
          )}
          {operation?.start_date && (
            <div className="stabilization_time">
              <Label label={getLabel("operationStartDate")}>
                {format(
                  new Date(operation?.start_date || null),
                  configs.dateFormat
                )}
              </Label>
              {operation?.completion_date && (
                <Label label={getLabel("stabilizationEndDate")}>
                  {format(
                    new Date(operation?.completion_date || null),
                    configs.dateFormat
                  )}
                </Label>
              )}
              {renderElapsedTime()}
            </div>
          )}
        </div>
      </div>
    );
  };

  const getUsedQuantity = (substance, batch) => {
    if (!substance) return;
    const quantity = batch?.quantity;
    const warehouseUnit = getWarehouseUnit(substance, getLabel);
    
    return ` (${quantity} ${warehouseUnit?.label})`;
  }

  const getIsEnoughStock = (drugId, batches, totalTreatmentVolume) => {
    const drug = drugs?.find((d) => d?.id === drugId);
    if (batches?.length > 0) {        
      const usedTotal = batches.reduce(
        (sum, b) => sum + (Number(b.quantity) || 0), 0) || 0;
      
      const warehouseUnit = getWarehouseUnit(drug, getLabel);
      const requiredNumericQty = computeRequiredNumericQty(drug, totalTreatmentVolume);
      const requiredWarehouseQty = computeRequiredWarehouseQty(warehouseUnit, requiredNumericQty);

      return  usedTotal >= requiredWarehouseQty;
    }
    return 0;
  }

  return (
    <>
     <h4>{getLabel("operationDetail")}</h4>
      <div className="operation_config_with_banner">
        <div className="config_section">
          {/* <h4>{getLabel(`operationType${operation?.type}`)}</h4> */}
          <div
            className={`operation_info_container_row ${
              operation?.cellarman_ids?.length > 1 ? "start" : ""
            }`}
          >
        <Label label={getLabel("insertionBy")}>
          <div>
            {users?.find?.(
              (user) => Number(user.id) === Number(operation?.user_id)
            )?.username || ""}
          </div>
        </Label>
        <Label label={getLabel("insertionDate")}>
          {format(
            new Date(operation?.insertion_date || null),
            configs.shortDateFormat
          )}
        </Label>
        {operation?.completion_date === null && (
          <>
            {(() => {
              const timing = getTreatmentTimingDisplay(operation, configs, getLabel);
              return (
                <Label
                  label={timing.fieldLabel}
                  sublabel={timing.sublabel || undefined}
                >
                  {timing.dateRange}
                </Label>
              );
            })()}
            <Label label={getLabel("cellarman")}>
              {
                <div className="cellarman_container">
                  {operation?.cellarman_ids?.map((cellarman, index) => {
                    const cellarmanName =
                      users?.find?.(
                        (user) => Number(user.id) === Number(cellarman)
                      )?.username || "";
                    return (
                      <div key={`cellarman-${index}`}>
                        {cellarmanName +
                          (operation?.cellarman_ids?.length > 1 ? " " : "")}
                      </div>
                    );
                  })}
                </div>
              }
            </Label>
          </>
        )}
        {operation?.completion_date && (
          <>
            <Label label={getLabel("operationCompletionUser")}>
              <div>
                {users?.find?.(
                  (user) => Number(user.id) === Number(operation?.completed_by)
                )?.username || ""}
              </div>
            </Label>
            {![
              "STABILIZATION_STATIC",
              "STABILIZATION_DYNAMIC",
              "STABILIZATION_RESIN",
            ].includes(operation?.type) && (
              <Label label={getLabel("operationCompletionDate")}>
                {format(
                  new Date(operation?.completion_date || null),
                  configs.dateFormat
                )}
              </Label>
            )}
          </>
        )}
        <Label label={getLabel("operationPriority")}>
          {getLabel(`operationPriority${operation?.priority}`)}
        </Label>
        {/* <Label label={getLabel("lotType")}>
          {operation?.batch_type ? getLabel(`lotType${operation?.batch_type}`) : ''}
        </Label> */}
      </div>
      
      {![
        "STABILIZATION_STATIC",
        "STABILIZATION_DYNAMIC",
        "STABILIZATION_RESIN",
      ].includes(type) && operation?.winemaking_protocol_id && (
        <div className="operation_info_container_row">
          <Label
            label={getLabel(
              (operation?.treatments?.[0]?.protocol_subtype === "PRE" || operation?.treatments?.[0]?.protocol_type === "VINIFICATION")
                ? "wineMakingProtocols"
                : "wineMakingPostProtocols"
            )}
          >
            {operation?.treatments?.[0]?.protocol_name || ""}
          </Label>
          {operation?.winemaking_protocol_id && <Label label={getLabel("protocolPhase")}>
            {operation?.treatments?.[0]?.phase
              ? getLabel(`protocolPhase_${operation?.treatments?.[0]?.phase}`)
              : ""}
          </Label>}
          {!operation?.winemaking_protocol_application_id && <Label label={getLabel("protocolNotes")}>
            {operation?.treatments?.[0]?.goal || "—"}
          </Label>}
          {!operation?.winemaking_protocol_application_id && <Label label={getLabel("protocolGoals")}>
            {operation?.treatments?.[0]?.goal || "—"}
          </Label>}
        </div>
      )}
        </div>
        <div className="banner_section">
          {protocolApplicationBanner}
        </div>
      </div>
      <h4>{getLabel("protocolOperation")} </h4>
      {["STABILIZATION_RESIN", "STABILIZATION_STATIC", "STABILIZATION_DYNAMIC"].includes(type) && renderStabilization()}
      {[
        "ADD",
        "HOMOGENIZATION_MASS",
        "OXYGENATION",
        "TANNISSAGE",
        "ENZYME",
        "SO2",
        "INOCULATION",
        "NITROGEN",
      ].includes(type) && (
        <>
          {["OXYGENATION", "FILTERING", "HOMOGENIZATION_MASS"].includes(type) && operation?.start_date && (
            <div className="operation_info_container_row">
              <div className="stabilization_time">
                <Label label={getLabel("operationStartDate")}>
                  {format(
                    new Date(operation?.start_date || null),
                    configs.dateFormat
                  )}
                </Label>
                {operation?.completion_date && (
                  <Label label={getLabel("operationCompletionDate")}>
                    {format(
                      new Date(operation?.completion_date || null),
                      configs.dateFormat
                    )}
                  </Label>
                )}
                {renderStartedOperationBadge()}
              </div>
            </div>
          )}
          <div className="operation_info_container_row">
            <div className="analysis_table">
              <TableEdit
                defaultData={tableData}
                columns={columns}
                isSearchBar={false}
                type={
                  isShowCost
                    ? "treatmentApplication"
                    : role === ROLES.USER
                    ? "treatmentApplicationUser"
                    : "treatment"
                }
                isLegend={false}
                // rowClickHandler={
                //   !isProtocolApplication
                //     ? (row) => {
                //         navigate(`/substances/${type}/${row.original.id}`);
                //       }
                //     : null
                // }
                isEditable={false}
                modalHandler={handleOpenModal}
              />
            </div>
          </div>
        </>
      )}
      {operation?.winemaking_protocol_id && type === "HOMOGENIZATION_MASS" && (
        <>
          <div className="operation_info_container_row">
            {operation?.treatments?.[0]?.protocol_type ===
              "HOMOGENIZATION_TEMP" && (
              <>
                <Label
                  label={getLabel("protocolOperation_HOMOGENIZATION_TEMP")}
                >
                  {`${operation?.treatments?.[0]?.quantity} \u00b0C`}
                </Label>
              </>
            )}
            {operation?.treatments?.[0]?.protocol_type ===
              "HOMOGENIZATION_REASSEMBLY" && (
              <>
                <Label label={getLabel("protocolHomogenizationType")}>
                  {getLabel(
                    `protocolHomogenizationMass_${operation?.treatments?.[0]?.homogenization_type}`
                  )}
                </Label>
                <Label label={getLabel("protocolHomogenizationWorkTime")}>
                  {operation?.treatments?.[0]?.work_time}
                </Label>
                <Label label={getLabel("protocolHomogenizationPauseTime")}>
                  {operation?.treatments?.[0]?.pause_time || operation?.treatments?.[0]?.homogenization_pause_time}
                </Label>
                <Label label={getLabel("protocolHomogenizationMotorSpeed")}>
                  {operation?.treatments?.[0]?.motor_speed || operation?.treatments?.[0]?.homogenization_motor_speed}
                </Label>
                <Label label={getLabel("protocolHomogenizationIterPerDay")}>
                  {operation?.treatments?.[0]?.iter_per_day}
                </Label>
              </>
            )}
          </div>
        </>
      )}
      {false && type === "OXYGENATION" &&   // false beacause already in the table data
        ["MICRO", "MACRO", "CUSTOM"].includes(
          operation?.treatments?.[0]?.protocol_type
        ) && (
          <>
            <div className="operation_info_container_row">
              <Label label={getLabel("protocolOxigenationTypology")}>
                {getLabel(
                  `protocolOXYGENATION_${operation?.treatments?.[0]?.protocol_type}`
                )}
              </Label>
              <Label label={getLabel("protocolOxygenationDoseNoUnit")}>
                {operation?.treatments?.[0]?.quantity || ""}{" "}
                {operation?.treatments?.[0]?.protocol_type === "CUSTOM"
                  ? `${getLabel("protocolOxygenationNoDose_CUSTOM")} ${
                      operation?.treatments?.[0]?.oxygenation_dose_hours || ""
                    }h:${
                      operation?.treatments?.[0]?.oxygenation_dose_minutes || ""
                    }m`
                  : getLabel(`protocolOxygenationNoDose_${operation?.treatments?.[0]?.protocol_type}`)}
              </Label>
              {/* {'CUSTOM' === operation?.treatments?.[0]?.protocol_type &&
            <Label
              label={getLabel("protocolOxygenationDoseCustomTime")}
            >
              {`$`}
            </Label>
            } */}
            </div>
          </>
        )}
      <div className="operation_info_container_title">
        <h4>{getLabel(operation?.src_tanks?.length > 1 ? "toTanks" : "toTank")}</h4>
        {["TANNISSAGE", "ENZYME", "SO2", "INOCULATION", "NITROGEN"].includes(
          type
        ) &&
          (role === 2 ||
            (role === ROLES.USER &&
              users?.filter((u) => u?.type === "USER")?.length === 0)) && (
            <CTA
              customClassName={!isShowCost ? "add" : "complete"}
              onClick={() => setIsShowCost(!isShowCost)}
            >
              {getLabel(
                isShowCost
                  ? "fromTanksAnalysisHideCost"
                  : "fromTanksAnalysisShowCost"
              )}
            </CTA>
          )}
      </div>
      <ul>
        {operation?.src_tanks?.map((srcTank, idx) => {
          return (
            <li key={`li-${idx}`}>
              <div className="operation_info_container_row">
                <div className="operation_info_container_row_total">
                  <TankOperationDetail
                    type={"AUTOMATIC"}
                    completed={completed}
                    role={role}
                    users={users}
                    tanks={tanks}
                    lots={lots}
                    tank={srcTank}
                    totalDrug={renderTotal(
                      tanks,
                      operation,
                      srcTank?.tank_id,
                      isShowCost,
                      watchedDrugBatches,
                      getLabel
                    )}
                    operation={operation}
                    idx={idx}
                    register={register}
                    errors={errors}
                    setValue={setValue}
                    getLabel={getLabel}
                    isSrc={true}
                    isMobile={isMobile}
                    virtualTanks={virtualTanks}
                    isVirtualMode={isVirtualMode}
                    isNextToExecute={isNextToExecute}
                  />
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
};

export const BottlingDetail = ({
  operation,
  completed,
  drops,
  users,
  tanks,
  lots,
  configs,
  getValues,
  register,
  errors,
  type,
  dispatch,
  setValue,
  user_id,
  axiosPrivate,
  isMobile,
  handleOpenModal,
  watchedDrugBatches,
  virtualTanks = null,
  isVirtualMode = false,
  isNextToExecute = false,
  protocolApplicationBanner = null,
}) => {
  const [getLabel] = useLabels();
  const { auth } = useAuth();
  const role = auth?.role || ROLES.GUEST;
  const [completedState, setCompletedState] = useState([]);
  const protocols = useSelector(selectWineMaking);
  const machinesData = useSelector(selectWarehouseByType("machineries"));
  const [isShowCost, setIsShowCost] = useState(false);

  const loadProtocols = async () => {
    const loadedProtocols = await getProtocols("winemaking", axiosPrivate);
    dispatch(setWineMaking(loadedProtocols));
  };

  const loadWarehouse = async (t) => {
    const currentData = await getWarehouseByType(axiosPrivate, t);
    dispatch(setWarehouse({ type: t, data: currentData }));
  };

  const columnHelper = createColumnHelper();

  const cipColumns = [
    columnHelper.accessor(
      (row) => row?.day + (row?.day_to ? ` - ${row?.day_to}` : ""),
      {
        header: getLabel("protocolStep"),
        cell: TableCell,
        meta: {
          type: "number",
          isFilterDisabled: true,
        },
      }
    ),
    columnHelper.accessor(
      (row) =>
        row?.operation
          ? row.operation === "ADD"
            ? computeAddOpTypeString(row)
            : getLabel(`protocolOperation_${row.operation}`)
          : "",
      {
        header: getLabel("protocolOperation"),
        cell: TableCell,
        meta: {
          type: "text",
          isFilterDisabled: true,
        },
      }
    ),
    columnHelper.accessor(
      (row) =>
        row?.operation === "SO2"
          ? ""
          : row.operation === "RINSE" || row.operation === "VAPORIZATION"
          ? getLabel("protocolProduct_H2O")
          : row?.product?.name || row?.product_name || "",
      {
        header: getLabel("protocolProduct"),
        cell: TableCell,
        meta: {
          type: "text",
          isFilterDisabled: true,
        },
      }
    ),
    columnHelper.accessor(
      (row) =>
        row?.quantity
          ? row.operation === "RINSE" || row.operation === "VAPORIZATION"
            ? ""
            : row?.operation === "CLEANING"
            ? `${row?.quantity} %`
            : ""
          : "",
      {
        header: getLabel("protocolConcentration"),
        cell: TableCell,
        meta: {
          type: "number",
          isFilterDisabled: true,
        },
      }
    ),
    columnHelper.accessor(
      (row) => (row?.work_time ? `${row?.work_time} min` : ""),
      {
        header: getLabel("protocolHomogenizationContactTime"),
        cell: TableCell,
        meta: {
          type: "text",
          isFilterDisabled: true,
        },
      }
    ),
  ];

  const columns = [
    // columnHelper.accessor(
    //   (row) => (row?.phase ? getLabel(`protocolPhase_${row.phase}`) : ""),
    //   {
    //     header: getLabel("protocolPhase"),
    //     cell: TableCell,
    //     meta: {
    //       isFilterDisabled: true,
    //     },
    //   }
    // ),
    // columnHelper.accessor(
    //   (row) =>
    //     row?.operation ? getLabel(`protocolOperation_${row.operation}`) : "",
    //   {
    //     header: getLabel("protocolOperation"),
    //     cell: TableCell,
    //     meta: {
    //       type: "text",
    //       isFilterDisabled: true,
    //     },
    //   }
    // ),
    columnHelper.accessor((row) => row?.product_name || "", {
      header: getLabel("drugName"),
      cell: TableCell,
      meta: {
        type: "text",
        isFilterDisabled: true,
      },
    }),
    columnHelper.accessor(
      (row) =>
        row?.functional ? getLabel(`drugType_${row?.type}`) : "",
      {
        header: getLabel("drugType"),
        cell: TableCell,
        meta: {
          type: "text",
          isFilterDisabled: true,
          filterVariant: "text",
        },
      }
    ),
    columnHelper.accessor(
      (row) =>
        row?.quantity
          ? row?.protocolType === "HOMOGENIZATION_TEMP"
            ? `${row?.quantity} \u00b0C`
            : (row?.quantity || 0) +
              " " +
              (row?.product?.unit || row?.product_unit || "") +
              (row?.protocolType === "MICRO"
                ? getLabel("protocolOxygenationMonth")
                : row?.protocolType === "MACRO"
                ? getLabel("protocolOxygenationDay")
                : row?.protocolType === "CUSTOM"
                ? `/${row?.oxygenation_dose_hours}h:${row?.oxygenation_dose_minutes}m`
                : "")
          : "",
      {
        header: getLabel("protocolQuantity"),
        cell: TableCell,
        meta: {
          type: "number",
          isFilterDisabled: true,
        },
      }
    ),

    // columnHelper.accessor(row => computeModifiedDrugBatches(row.drug_batches, getValues("drugbatches"))
    // //  Array.isArray(row.drug_batches)
    // //   ? [...new Set(row.drug_batches.map(b => b.batch_number))].join(", ")
    // //   : ""
    // , {
    //   header: getLabel("drugBatchNumbers"),
    //   cell: TableCell,
    //   meta: {
    //     isFilterDisabled: true,
    //   },
    // }),
    columnHelper.accessor("drug_batches", {
      header: getLabel("drugBatchNumbers"),
      cell: ({ row }) => {
        const { substance_id, drug_batches: originalBatches } = row.original;

        const modifiedBatches = watchedDrugBatches?.[substance_id] || [];
        const effectiveBatches =
          (Array.isArray(modifiedBatches) && modifiedBatches?.length > 0
            ? modifiedBatches
            : originalBatches) || [];
        
        return [...new Set(effectiveBatches.map(b => b.batch_number))].join(", ");
      },
      meta: {
        isFilterDisabled: true,
      },
    }),

    // columnHelper.accessor("batch_row", {
    //   header: getLabel("drugBatchNumber"),
    //   cell: TableCell,
    //   meta: {
    //     type: "select",
    //     control: control,
    //     editable: true,
    //     isFilterDisabled: true,
    //     dateFormat: configs?.shortDateFormat,
    //     options: drugs,
    //     options2: getValues("drug_batches") || [],
    //   },
    // }),
  ];

  if (isShowCost) {
    columns.push(
      columnHelper.accessor(
        (row) =>
          `${row?.drug_batch_cost || ""} ${row?.drug_batch_cost ? "€" : ""}`,
        {
          header: getLabel("drugBatchProductCost"),
          cell: TableCell,
          meta: {
            isFilterDisabled: true,
          },
        }
      )
    );
  }

  if (
    operation?.completion_date === null &&
    ((Number(role) === ROLES.USER && operation?.cellarman_ids?.includes(auth?.user_id)) ||
      (Number(role) === ROLES.CLIENT && users?.filter((u) => u?.type === "USER")?.length === 0))) {
      columns.push(
        columnHelper.display({
          id: "edit",
          cell: EditCell,
          meta: {
            isEditable: true,
            type: "drugBatchUpdate"
          },
        })
      );
  }

  useEffect(() => {
    if (!protocols || protocols?.length === 0) loadProtocols();
    if (!machinesData || machinesData.length === 0) {
      loadWarehouse("machineries");
    }
  }, []);

  const getWinemakingProtocolTreatments = () => {
    return aggregateDrugsById((operation?.treatments || [])?.map(t =>
      {
        return {
          ...t,
          functional: t?.protocol_type,
          product_unit: t?.unit,
          product_name: t?.substance_name,
          opType: "TREATMENTS",
          // drug_batches: (t?.drug_batches || [])?.map(b => b?.batch_number)
        }
      }
      )
    );
    // protocols
    //   ?.find((p) => p.id == operation?.winemaking_protocol_id)
    //   ?.operations?.map((s) => {
    //     return {
    //       ...s,
    //       opType: "WINEMAKINGPROTOCOL",
    //     };
    //   }) || []
  }

  const setCheckboxState = (batch_id) => {
    const newCompletedState = [...completedState];
    const index = newCompletedState.indexOf(batch_id);
    if (index === -1) {
      newCompletedState.push(batch_id);
    } else {
      newCompletedState.splice(index, 1);
    }
    setCompletedState(newCompletedState);
  };

  const computeAddOpTypeString = (row) => {
    if (row?.treatments?.length > 1) {
      return getLabel(`operationType${row.type}`);
    }
    const protocol_type = row?.functional || null;
    const subtype = protocol_type
      ? getLabel(`drugLabelGroup_${protocol_type}`)
      : null;
    return `${getLabel(`protocolType_${row.operation}`)} ${
      subtype ? "(" + subtype?.toLowerCase?.() + ")" : ""
    }`;
  };

  return (
    <>
      {protocolApplicationBanner}
      {/* <h3>{getLabel(`operationType${operation?.type}`)}</h3> */}
      <div
        className={`operation_info_container_row ${
          operation?.cellarman_ids?.length > 1 ? "start" : ""
        }`}
      >
        <Label label={getLabel("insertionBy")}>
          <div>
            {users?.find?.(
              (user) => Number(user.id) === Number(operation?.user_id)
            )?.username || ""}
          </div>
        </Label>
        <Label label={getLabel("insertionDate")}>
          {format(
            new Date(operation?.insertion_date || null),
            configs.shortDateFormat
          )}
        </Label>
        {operation?.completion_date === null && (
          <Label label={getLabel("expireDate")}>
            {format(
              new Date(operation?.expire_date || null),
              configs.shortDateFormat
            )}
          </Label>
        )}
        {operation?.completion_date && (
          <>
            <Label label={getLabel("operationCompletionUser")}>
              <div>
                {users?.find?.(
                  (user) => Number(user.id) === Number(operation?.completed_by)
                )?.username || ""}
              </div>
            </Label>
            <Label label={getLabel("operationCompletionDate")}>
              {format(
                new Date(operation?.completion_date || null),
                configs.dateFormat
              )}
            </Label>
          </>
        )}
        <Label label={getLabel("cellarman")}>
          {
            <div className="cellarman_container">
              {operation?.cellarman_ids?.map((cellarman, index) => {
                const cellarmanName =
                  users?.find?.((user) => Number(user.id) === Number(cellarman))
                    ?.username || "";
                return (
                  <div key={`cellarman-${index}`}>
                    {cellarmanName +
                      (operation?.cellarman_ids?.length > 1 ? " " : "")}
                  </div>
                );
              })}
            </div>
          }
        </Label>
        <Label label={getLabel("operationPriority")}>
          {getLabel(`operationPriority${operation?.priority}`)}
        </Label>
      </div>
      <div className={"confirmation-modal-container"}>
        <h4>{getLabel("operationDetail")}</h4>
        <OperationRecap
          type={"BOTTLING"}
          isScale={false}
          // src_tanks={getRecapTanks(tanks, operation, true)}
          src_tanks={operation?.src_tanks}
          dest_tanks={operation?.dest_tanks}
        />
      </div>
      <h5>{getLabel("fromTank")}</h5>
      <TankOperationDetail
        isSrc={true}
        tank={operation?.src_tanks?.[0]}
        operation={operation}
        register={register}
        errors={errors}
        idx={0}
        tanks={tanks}
        users={users}
        lots={lots}
        role={role}
        completed={completed}
        completedState={completedState}
        setCheckboxState={setCheckboxState}
        setValue={setValue}
        getLabel={getLabel}
        isMobile={isMobile}
        virtualTanks={virtualTanks}
        isVirtualMode={isVirtualMode}
        isNextToExecute={isNextToExecute}
      />
      <h5>{getLabel("toBottles")}</h5>
      {operation?.dest_tanks?.map((destTank, index) => {
        return (
          <TankOperationDetail
            isSrc={true}
            tank={destTank}
            operation={operation}
            register={register}
            errors={errors}
            idx={index}
            type={"BOTTLING"}
            totalDrug={renderTotal(
              tanks,
              operation,
              destTank?.transfer_id,
              isShowCost,
              watchedDrugBatches,
              getLabel
            )}
            tanks={tanks}
            users={users}
            lots={lots}
            role={role}
            completed={completed}
            drops={drops}
            completedState={completedState}
            setCheckboxState={setCheckboxState}
            setValue={setValue}
            getLabel={getLabel}
            isMobile={isMobile}
            virtualTanks={virtualTanks}
            isVirtualMode={isVirtualMode}
            isNextToExecute={isNextToExecute}
          />
        );
      })}

      <h4>{getLabel("protocolBottlingCleaningTitle")}</h4>
      <Label label={getLabel("protocolBottlingMachine")}>
        {machinesData?.find((m) => m?.id === operation?.machinery_id)?.name ||
          ""}
      </Label>
      {operation?.cleaning_protocol_id && (
        <div className="operation_info_container_row vertical">
          <TableEdit
            defaultData={
              protocols
                ?.find((p) => p.id == operation?.cleaning_protocol_id)
                ?.operations?.map((s) => {
                  return {
                    ...s,
                    opType: "WINEMAKINGPROTOCOL",
                  };
                }) || []
            }
            columns={cipColumns}
            isEditable={false}
            isSearchBar={false}
            isLegend={false}
            type={"winemaking"}
          />
        </div>
      )}
      <h4>{getLabel("protocolBottlingTitle")}</h4>
      {operation?.winemaking_protocol_id && (
        <div className="operation_info_container_row vertical">
          <TableEdit
            defaultData={getWinemakingProtocolTreatments()}
            columns={columns}
            isEditable={false}
            isSearchBar={false}
            isLegend={false}
            // type={"winemaking"}
            type={
              isShowCost
                ? "treatmentApplication"
                : role === ROLES.USER
                ? "treatmentApplicationUser"
                : "treatment"
            }
            modalHandler={handleOpenModal}
          />
        </div>
      )}
      {operation?.winemaking_protocol_id && (
        <div className="operation_info_container_title additional">
          <h4></h4>
          {(role === 2 ||
            (role === ROLES.USER &&
              users?.filter((u) => u?.type === "USER")?.length === 0)) && (
            <CTA
              customClassName={!isShowCost ? "add" : "complete"}
              onClick={() => setIsShowCost(!isShowCost)}
            >
              {getLabel(
                isShowCost
                  ? "fromTanksAnalysisHideCost"
                  : "fromTanksAnalysisShowCost"
              )}
            </CTA>
          )}
          {/* {(role === 3 && operation?.cellarman_ids?.includes(auth?.user_id)) && (
            <CTA
              customClassName={"change_drug_batches_button"}
              onClick={() => setIsShowCost(!isShowCost)}
            >
              {getLabel("cellarmanChangeDrugBatches")}
            </CTA>
          )} */}
        </div>
      )}
      {/* <div className="operation_info_container_row">
        <Label label={getLabel("operationNotes")} width={400} height={150} customClassName={"notes"}>
          {operation?.note}
        </Label>
      </div> */}
      {/* <div className="operation_info_container_row">
        <ChatArea
          name="chat"
          register={register}
          label={getLabel("lotNotes")}
          placeholder={getLabel("lotNotesPlaceholder")}
          users={users}
          user_id={user_id}
          // tooltip={getLabel("lotNotesTooltip")}
          configs={configs}
          operation={operation}
          storeNote={() =>
            storeNote(
              axiosPrivate,
              dispatch,
              setValue,
              getValues,
              getLabel,
              operation,
              user_id,
            )
          }
        />
      </div>
      <div className="operation_info_container_row additional">
        <Label label={getLabel("demandant")}>
          {users?.find?.(
            (user) => Number(user.id) === Number(operation?.user_id)
          )?.username || ""}
        </Label>
        <Label label={getLabel("updateDate")}>
          {format(new Date(operation?.update_date || null), configs.dateFormat)}
        </Label>
      </div> */}
    </>
  );
};

export const TreatmentsLabel = ({
  tankList,
  drugConcentrations,
  isDrugConcentration,
  setIsDrugConcentration,
  isAdjuvant,
  setIsAdjuvant,
  labelTankId,
  setLabelTankId,
  filteredDrugs,
  isTankDetail = false,
}) => {
  const [getLabel] = useLabels();

  const hasMultipleTanks = tankList?.length > 1;
  const hasDrugs = drugConcentrations?.length > 0;
  const hasFilteredDrugs = filteredDrugs?.length > 0;

  return (
    <div className="treatments_label_container">
      {/* Header */}
      <div className="treatments_label_header">
        {!isTankDetail ? (
          <h4 className="treatments_label_title">{getLabel("lotWineLabel")}</h4>
        ) : (
          <h5 className="treatments_label_title">{""}</h5>
          // <h5 className="treatments_label_title">{getLabel("lotWineLabel")}</h5>
        )}
      </div>

      {/* Tank Selector */}
      {hasMultipleTanks && (
        <div className="tank_selector_wrapper">
          <MultiSelector
            list={tankList}
            param="name"
            id={labelTankId}
            setId={setLabelTankId}
            getLabel={getLabel}
          />
        </div>
      )}

      {/* Content */}
      <div className="treatments_label_content">
        <div className={`treatments_label_card ${isTankDetail ? "tank_detail" : ""}`}>
          {/* Empty State */}
          {!hasDrugs && (
            <div className="treatments_empty_state">
              <Icon icon="fa-prescription-bottle" className="treatments_empty_icon" />
              <p className="treatments_empty_text">
                {getLabel("lotNoCompositionsData")}
              </p>
            </div>
          )}

          {/* Treatments List */}
          {hasDrugs && (
            <div className="treatments_label_wrapper">
              {/* Control Buttons */}
              <div className="treatments_controls">
                <Tooltip
                  variant={"info"}
                  html={getLabel(
                    !hasFilteredDrugs
                      ? "lotNoCompositionsDataOnlyAdjuvantTooltip"
                      : !isDrugConcentration
                      ? "lotShowCompositions"
                      : "lotHideCompositions"
                  )}
                  place="left"
                  events={["hover"]}
                >
                  <button
                    className={`treatments_control_btn ${isDrugConcentration ? "active" : ""}`}
                    onClick={() => setIsDrugConcentration((prev) => !prev)}
                    disabled={!hasFilteredDrugs}
                    aria-label={getLabel(
                      isDrugConcentration ? "lotHideCompositions" : "lotShowCompositions"
                    )}
                  >
                    <Icon
                      size="xl"
                      icon={isDrugConcentration ? "fa-tags" : "fa-tag"}
                    />
                  </button>
                </Tooltip>
                {hasFilteredDrugs && (
                  <Tooltip
                    variant={"info"}
                    html={getLabel(
                      !isAdjuvant ? "lotShowAdjuvants" : "lotHideAdjuvants"
                    )}
                    place="left"
                    events={["hover"]}
                  >
                    <button
                      className={`treatments_control_btn ${isAdjuvant ? "active" : ""}`}
                      onClick={() => setIsAdjuvant((prev) => !prev)}
                      aria-label={getLabel(
                        isAdjuvant ? "lotHideAdjuvants" : "lotShowAdjuvants"
                      )}
                    >
                      <Icon
                        size="xl"
                        icon="fa-droplet"
                      />
                    </button>
                  </Tooltip>
                )}
              </div>

              {/* Label Card */}
              <div className={`treatments_label_box ${isTankDetail ? "all_borders" : ""}`}>
                <div className="treatments_label_header_box">
                  <h5 className="treatments_label_box_title">
                    {getLabel("lotWineLabelAdjuvants")}
                  </h5>
                </div>

                <ul className="treatments_list">
                  {filteredDrugs?.map((item, index) => (
                    <li key={`treatment-${index}`} className="treatments_list_item">
                      <span className="treatments_item_name">
                        {(isDrugConcentration
                          ? getLabel(`drugLabel_${item?.ingredient_name}`)
                          : item?.product_name) || ""}
                      </span>
                      <span className="treatments_item_separator">:</span>
                      <span className="treatments_item_amount">
                        {`${parseFloat(item?.quantity?.toFixed(3)) || "0"}`}
                        <span className="treatments_item_unit">{item.unit || ""}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const MultiSelector = ({ list = [], param, id, setId, getLabel = () => "" }) => {
  return (
    <div className="tank_selector">
      {list?.map((item, index) => {
        const isSelected = item.id === Number(id);
        const isFirst = index === 0;
        const isLast = index === list.length - 1;

        return (
          <button
            key={item.id}
            type="button"
            className={`tank_selector_item ${isSelected ? "selected" : ""} ${isFirst ? "first" : ""} ${isLast ? "last" : ""}`}
            onClick={(e) => {
              setId(item.id);

              // Scroll selected item into view
              e.currentTarget.scrollIntoView({
                behavior: "smooth",
                inline: "center",
                block: "nearest",
              });
            }}
            aria-label={`${getLabel("selectTank")} ${item?.[param]}`}
            aria-pressed={isSelected}
          >
            <span className="tank_selector_item_text">{item?.[param]}</span>
          </button>
        );
      })}
    </div>
  );
};
